import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import JobWorkChallans from '../components/JobWorkChallans';
import { ListFooter } from '../ui';
import printDocument, { printBtnStyle } from '../utils/print';

// Job Work — process outsourcing. RSR (per piece) / Deroi (per KG).
// JWO → collected → processing → delivered → Q&Q + GRN → invoice approved.

const API_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const fileHref = (u) => !u ? '#' : (/^https?:\/\//.test(u) ? u : (u.startsWith('/api/') ? API_ORIGIN + u : u));
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
// Print wants the year: a document that outlives the screen cannot say "08 Aug".
const fmtDLong = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
// printDocument renders ₹ natively in the browser, so the glyph is safe here.
const inr = (v) => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const RSR_PROCESSES = [['embroidery', 'Embroidery'], ['printing', 'Printing'], ['washing', 'Washing'], ['special_finishing', 'Special Finishing']];
const DEROI_PROCESSES = [['knitting', 'Knitting'], ['dyeing', 'Dyeing'], ['washing', 'Washing'], ['finishing', 'Finishing']];
const PROC_LABEL = Object.fromEntries([...RSR_PROCESSES, ...DEROI_PROCESSES]);
// 'paid' is a display stage derived from the invoice (JWO status tops out at 'invoiced').
const PIPELINE = ['pending', 'collected', 'processing', 'delivered', 'grn_raised', 'invoiced', 'paid'];
const PIPE_SHORT = { pending: 'Pending', collected: 'Collected', processing: 'Processing', delivered: 'Delivered', grn_raised: 'GRN', invoiced: 'Invoiced', paid: 'Paid' };
const STATUS_TAG = {
  pending: { bg: '#f0f2f8', c: '#666', label: 'Pending' },
  collected: { bg: '#EEEDF8', c: '#3B3A8F', label: 'Collected' },
  processing: { bg: '#FEF0E6', c: '#C2410C', label: 'Processing' },
  delivered: { bg: '#FFF8E6', c: '#9A6B00', label: 'Delivered' },
  grn_raised: { bg: '#E0F2F1', c: '#0d9488', label: 'GRN Raised' },
  invoiced: { bg: '#EAF7EE', c: '#166534', label: 'Invoiced' },
  paid: { bg: '#EEEDF8', c: '#3B3A8F', label: 'Paid ✓' },
  cancelled: { bg: '#f0f0f0', c: '#999', label: 'Cancelled' },
};
// Effective display status: a JWO whose invoice is paid shows as 'paid'.
const effStatus = (j) => (j.status === 'invoiced' && j.invoice_status === 'paid') ? 'paid' : j.status;
// mig 193 vocabulary. 'conditional' stays amber — it is a real third outcome, not an
// unknown value; the `|| Q_TAG.conditional` fallback at the call site means anything
// unrecognised also renders amber, which is the right way round for a quality badge.
const Q_TAG = { passed: { bg: '#EAF7EE', c: '#166534' }, failed: { bg: '#FCEBEB', c: '#A32D2D' }, conditional: { bg: '#FFF8E6', c: '#9A6B00' } };

const inputStyle = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const labelStyle = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const btnPrimary = { padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };

const tag = (t) => ({ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: t.bg, color: t.c, whiteSpace: 'nowrap' });

// Labeled status pipeline: Pending → Collected → Processing → Delivered → GRN → Invoiced → Paid.
const Pipeline = ({ status, size = 'sm' }) => {
  const idx = PIPELINE.indexOf(status);
  if (status === 'cancelled') return <span style={tag(STATUS_TAG.cancelled)}>Cancelled</span>;
  const big = size === 'lg';
  return (
    <div style={{ display: 'flex', gap: big ? 0 : 3, alignItems: 'flex-start' }}>
      {PIPELINE.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: big ? 76 : 'auto' }}>
            <div style={{
              width: big ? 16 : 10, height: big ? 16 : 10, borderRadius: '50%',
              background: i <= idx ? (STATUS_TAG[s] || {}).c || '#3B3A8F' : '#e5e7eb',
              border: i === idx ? `2px solid ${(STATUS_TAG[s] || {}).c}` : '2px solid transparent',
              boxShadow: i === idx ? '0 0 0 3px ' + ((STATUS_TAG[s] || {}).bg || '#eee') : 'none' }} />
            <span style={{ fontSize: big ? 10 : 7, marginTop: 2, fontWeight: i === idx ? 800 : 500, color: i <= idx ? (STATUS_TAG[s] || {}).c : '#bbb', whiteSpace: 'nowrap' }}>
              {PIPE_SHORT[s]}</span>
          </div>
          {i < PIPELINE.length - 1 && <div style={{ height: 2, width: big ? 18 : 8, background: i < idx ? (STATUS_TAG[status] || {}).c : '#e5e7eb', marginTop: big ? 7 : 4 }} />}
        </div>
      ))}
    </div>
  );
};

// Next-step action buttons for a JWO — shared by the table rows and detail modal.
// canReceive is a strict superset of canManage's holders for the GRN action only. A store
// user arrives with canManage=false and canReceive=true: it must reach Raise GRN and no
// other button, so every manage-only action below is now gated explicitly rather than
// relying on the single early return.
function JwoActions({ j, canManage, canReceive = false, isMgmt, setStatus, onRaiseGrn, onUploadInvoice, onChanged, size = 'sm' }) {
  const pad = size === 'lg' ? '7px 16px' : '4px 10px';
  const fs = size === 'lg' ? 12 : 10;
  const markPaid = async () => {
    try { await api.patch(`/jobwork/invoices/${j.invoice_id}/approve`, { status: 'paid' }); onChanged(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const [rateFor, setRateFor] = useState(null);
  const st = effStatus(j);
  if (!canManage && !canReceive) return st === 'paid' ? <span style={{ fontSize: fs + 1, color: '#166534', fontWeight: 700 }}>✅ Completed</span> : null;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {rateFor && <RateCorrectionModal j={rateFor} onClose={() => setRateFor(null)} onSaved={() => { setRateFor(null); onChanged && onChanged(); }} />}
      {canManage && st === 'pending' && <button onClick={() => setStatus(j, 'collected')} style={{ ...btnPrimary, padding: pad, fontSize: fs, background: '#EEEDF8', color: '#3B3A8F' }}>📦 Mark Collected</button>}
      {canManage && st === 'collected' && <button onClick={() => setStatus(j, 'processing')} style={{ ...btnPrimary, padding: pad, fontSize: fs, background: '#FEF0E6', color: '#C2410C' }}>⚙️ Processing</button>}
      {canManage && ['collected', 'processing'].includes(st) && <button onClick={() => setStatus(j, 'delivered')} style={{ ...btnPrimary, padding: pad, fontSize: fs, background: '#FFF8E6', color: '#9A6B00', marginLeft: 4 }}>🚚 Mark Delivered</button>}
      {/* CANCEL — pending and collected only. The route also permits 'processing', but by then
          the vendor has the goods and is working on them; that is a return or a write-off
          conversation, not a cancellation. Deliberately last in the row and grey, so it does
          not sit beside the forward actions as an equal choice. */}
      {canManage && ['pending', 'collected'].includes(st) && <button onClick={() => setStatus(j, 'cancelled')} title="Cancel this job work order — a written reason is required" style={{ ...btnPrimary, padding: pad, fontSize: fs, background: '#f0f0f0', color: '#A32D2D', marginLeft: 8 }}>✕ Cancel</button>}
      {/* ── STATUS ALONE IS NOT THE TEST ────────────────────────────────────────────────────
          jw.status does NOT move on receipt: it advances delivered -> grn_raised on the QUALITY
          VERDICT (mig 189). So "delivered" means "received or not yet received", and this button
          offered the action already taken. JWO-RSR-1806-001 sat with a GRN for 728 of 728 and a
          page still inviting somebody to raise one.
          The test is the GRN ROW. Where one exists and nobody has judged it, the outstanding
          action is the verdict — so that is what is offered. */}
      {canReceive && st === 'delivered' && !j.grn_no && <button onClick={() => onRaiseGrn(j)} style={{ ...btnPrimary, padding: pad, fontSize: fs, background: '#166534' }}>📥 Raise GRN</button>}
      {st === 'delivered' && j.grn_no && !j.quality_status &&
        <span style={{ fontSize: fs, color: '#9A6B00', fontWeight: 700 }}
          title={j.type === 'deroi'
            ? `${j.grn_no} — the goods are received. The greige verdict is recorded PER LOT MEMBER under Quality → 🧵 Greige QC, not on this GRN row (ruling 6). QA cannot see this row in any case: the GRN list is scoped to their own book.`
            : `${j.grn_no} — the goods are received. What is outstanding is the QUALITY VERDICT, signed by QA on the GRN tab. The invoice stays blocked until it is.`}>
          ⏳ Received — awaiting quality{j.type === 'deroi' ? ' (Greige QC)' : ''}
        </span>}
      {canManage && st === 'grn_raised' && !j.invoice_id && <button onClick={() => onUploadInvoice(j)} style={{ ...btnPrimary, padding: pad, fontSize: fs }}>🧾 Upload Invoice</button>}
      {/* The decision on an invoice is its CERTIFICATION (15 Oct): the order is 'invoiced' from the
          moment the invoice is entered, so these read the latest invoice's review_status, not the order. */}
      {j.invoice_id && j.invoice_review_status === 'pending' && <span style={{ fontSize: fs, color: '#9A6B00', fontWeight: 700 }}>⏳ Invoice awaiting certification (Invoices tab)</span>}
      {j.invoice_id && j.invoice_review_status === 'rejected' && st !== 'paid' && <>
        <span style={{ fontSize: fs, color: '#A32D2D', fontWeight: 700 }} title="A rejected invoice is not reopened. The vendor's corrected bill is entered as a new invoice; the rejected one stays on file.">✗ Invoice {j.invoice_no} rejected</span>
        {canManage && <button onClick={() => onUploadInvoice(j)} style={{ ...btnPrimary, padding: pad, fontSize: fs, marginLeft: 6 }}>🧾 Enter corrected invoice</button>}
      </>}
      {st === 'invoiced' && j.invoice_review_status === 'approved' && (isMgmt
        ? <button onClick={markPaid} style={{ ...btnPrimary, padding: pad, fontSize: fs, background: '#3B3A8F' }}>₹ Mark Paid</button>
        : <span style={{ fontSize: fs, color: '#166534', fontWeight: 700 }}>Certified — payment pending</span>)}
      {st === 'paid' && <span style={{ fontSize: fs + 1, color: '#166534', fontWeight: 800 }}>✅ Completed</span>}
      {/* RATE CORRECTION (mig 382). Shown on every order a manager can act on, locked or not: a locked
          order opens to say WHICH lock and why, rather than the control vanishing (checklist 18). */}
      {canManage && <button onClick={() => setRateFor(j)} title="Correct this order's rate — a personal login and a written reason are required; the old rate is kept"
        style={{ ...btnPrimary, padding: pad, fontSize: fs, background: Number(j.rate) > 0 ? '#f0f0f0' : '#FCEBEB', color: Number(j.rate) > 0 ? '#555' : '#A32D2D', marginLeft: 8 }}>₹ Correct rate</button>}
    </span>
  );
}

// A rate is CORRECTED, never overwritten (mig 382): old and new are both kept, with who and why, and any
// cost already posted at the old rate is reversed and re-posted. The server decides the lock; this asks
// it first so the lock is said before anybody types a reason.
function RateCorrectionModal({ j, onClose, onSaved }) {
  const [info, setInfo] = useState(null);
  const [rate, setRate] = useState('');
  const [reason, setReason] = useState('');
  const [capReason, setCapReason] = useState('');
  const [needsCapReason, setNeedsCapReason] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get(`/jobwork/${j.id}/rate-correction`).then(r => setInfo(r.data.data))
      .catch(e => setError(e.response?.data?.error || 'Could not load this order'));
  }, [j.id]);
  const unitLabel = j.unit === 'PCS' ? 'piece' : 'KG';
  const save = async () => {
    setError('');
    if (!(parseFloat(rate) > 0)) return setError(`The corrected rate must be more than ₹0 per ${unitLabel}.`);
    if (reason.trim().length < 20) return setError('Say why, in at least 20 characters — what the agreed rate is and where it comes from.');
    setBusy(true);
    try {
      await api.post(`/jobwork/${j.id}/rate-correction`, { rate, reason, ...(capReason.trim() ? { cap_exception_reason: capReason.trim() } : {}) });
      onSaved();
    } catch (e) {
      const d = e.response?.data || {};
      if (d.exception_available) setNeedsCapReason(true);
      setError(d.error || 'Failed'); setBusy(false);
    }
  };
  const lock = info?.lock;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2100, overflowY: 'auto', padding: '24px 0', whiteSpace: 'normal' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 520, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', textAlign: 'left' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 16px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Correct rate — {j.jwo_no}</h3></div>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#555' }}>
          {j.process_type} · {j.vendor_name} · {Number(j.qty_sent || 0).toLocaleString('en-IN')} {j.unit || ''} · current rate <strong>₹{Number(info?.rate ?? j.rate ?? 0).toFixed(2)}</strong>/{unitLabel}</p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {!info && !error && <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>}
        {lock && <div style={{ background: '#FEF3C7', color: '#92400E', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 600 }}>🔒 {lock.text}</div>}
        {info && !lock && (<>
          <label style={labelStyle}>Corrected rate (₹ per {unitLabel}) *</label>
          <input type="number" step="0.01" min="0.01" style={{ ...inputStyle, width: '100%', marginBottom: 10 }} value={rate} onChange={e => setRate(e.target.value)} />
          <label style={labelStyle}>Reason * (at least 20 characters)</label>
          <textarea rows={3} style={{ ...inputStyle, width: '100%', resize: 'vertical' }} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="What rate was agreed, with whom, and where it is written down" />
          {needsCapReason && (<>
            <label style={{ ...labelStyle, marginTop: 10 }}>Management exception — why this rate may exceed the approved estimate *</label>
            <textarea rows={2} style={{ ...inputStyle, width: '100%', resize: 'vertical' }} value={capReason} onChange={e => setCapReason(e.target.value)} />
          </>)}
          <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
            The old rate is kept, with your name and the reason. Cost already booked at the old rate is reversed and re-booked at the new one. A shared login cannot correct a rate.</div>
        </>)}
        {info?.corrections?.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 11, color: '#555' }}><strong>Earlier corrections</strong>
            {info.corrections.map(c => <div key={c.id}>₹{Number(c.old_rate).toFixed(2)} → ₹{Number(c.new_rate).toFixed(2)} · {c.corrected_by_name} · {new Date(c.corrected_at).toLocaleString('en-IN')} — {c.reason}</div>)}</div>)}
        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>{lock ? 'Close' : 'Cancel'}</button>
          {info && !lock && <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? '…' : '💾 Record correction'}</button>}
        </div>
      </div>
    </div>
  );
}

function FileBtn({ onUploaded, label }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(r.data.url, f.name);
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <label style={{ ...btnPrimary, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', display: 'inline-block' }}>
      {busy ? 'Uploading…' : label}
      <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={upload} />
    </label>
  );
}

export default function JobWork() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMgmt = ['admin', 'management'].includes(user?.role);
  // §E — deroi_operator's gate is PER TYPE, on both sides. canManage used to be one
  // global boolean covering the RSR tab, the Deroi tab, GRN and Invoices alike, so
  // simply adding deroi_operator to it would have handed them RSR job-work
  // management: creating RSR JWOs against suppliers, raising RSR GRNs, uploading
  // RSR invoices. The server enforces the same rule (jwoScopeError in
  // jobwork.routes.js) — this is the UI half of it, not the control itself.
  const MANAGE_BASE = ['admin', 'management', 'procurement_officer', 'merchandiser'];
  const isBaseManager = MANAGE_BASE.includes(user?.role);
  const isDeroiOperator = user?.role === 'deroi_operator';
  // Capability against a specific JWO's type — the form used on per-row actions.
  const canManageType = (t) => isBaseManager || (isDeroiOperator && t === 'deroi');
  // Capability for a whole tab. rsr/grn/invoices span both companies, so only a
  // base manager gets them; the Deroi tab is a Deroi surface.
  const canManageTab = (t) => (t === 'deroi' ? canManageType('deroi') : isBaseManager);
  // mig 189 — the quality verdict is a separate act by a separate person. The
  // receiver cannot sign their own receipt; admin/management may break glass
  // and the server records that they did.
  const canSignQuality = ['admin', 'management', 'quality_incharge', 'qc_inspector'].includes(user?.role);
  // Store 1 receives and nothing else. Deliberately a SEPARATE capability from canManageType:
  // folding 'store' into MANAGE_BASE would have handed it New JWO, Add Batch, Upload Invoice
  // and the Deroi tab — the same mistake §E above describes for deroi_operator. Scoped to
  // 'rsr' to mirror jwScope(), which maps store to the everyone-else default server-side.
  const isStore = user?.role === 'store';
  const canReceiveType = (t) => canManageType(t) || (isStore && t === 'rsr');
  const [tab, setTab] = useState('rsr');
  const [jwos, setJwos] = useState([]);
  const [tat, setTat] = useState({});   // jwo_id → its clock (mig 298)
  const [grns, setGrns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deroiVendors, setDeroiVendors] = useState([]);   // Deroi's own vendor register (for deroi-type JWOs)
  const [gpos, setGpos] = useState([]);
  const [filters, setFilters] = useState({});
  const [showNew, setShowNew] = useState(false);
  // Deep-link from the Deroi challan screen: ?tab=<key> lands on that tab,
  // &new=1 opens the New JWO modal (only for rsr/deroi, which allow creation).
  // Params are cleared once handled so a refresh doesn't re-open the modal.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const t = sp.get('tab'), openNew = sp.get('new');
    if (t && ['rsr', 'deroi', 'grn', 'invoices', 'material'].includes(t)) setTab(t);
    if (openNew === '1' && ['rsr', 'deroi'].includes(t)) setShowNew(true);
    if (t || openNew) navigate(location.pathname, { replace: true });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [grnJwo, setGrnJwo] = useState(null);
  const [qualityGrn, setQualityGrn] = useState(null);   // GRN awaiting a verdict (mig 189)
  const [invJwo, setInvJwo] = useState(null);
  const [gstFor, setGstFor] = useState(null);   // mig 413: an invoice entered before GST was recorded
  // Certification (15 Oct): the invoice whose Reject is open, and the reason being typed.
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectWhy, setRejectWhy] = useState('');
  const certify = async (i, decision, remarks) => {
    try {
      await api.post(`/jobwork/invoices/${i.id}/certify`, decision === 'rejected' ? { decision, remarks } : { decision });
      setRejectFor(null); setRejectWhy(''); load();
    } catch (e) { alert(e.response?.data?.error || 'Certification failed'); }
  };
  const [detailId, setDetailId] = useState(null);

  const [groups, setGroups] = useState({ rsr: [], deroi: [] });
  const [collapsed, setCollapsed] = useState({});  // groupKey -> true (batches hidden)
  const [newLock, setNewLock] = useState(null);   // prefill/lock for Add Batch

  // Material challans + register are scoped by company per the role:
  // admin/management toggle both; RSR users → RSR; deroi staff → Deroi.
  // (Deroi staff normally reach this via the Deroi module; the server enforces scope too.)
  const materialLock = ['admin', 'management'].includes(user?.role) ? null
    : user?.role === 'deroi_operator' ? 'deroi' : 'rsr';
  const load = useCallback(async () => {
    const [j, g, i, gr, gd, t] = await Promise.all([
      api.get('/jobwork').catch(() => ({ data: { data: [] } })),
      api.get('/jobwork/grn').catch(() => ({ data: { data: [] } })),
      api.get('/jobwork/invoices').catch(() => ({ data: { data: [] } })),
      api.get('/jobwork/groups?type=rsr').catch(() => ({ data: { data: [] } })),
      api.get('/jobwork/groups?type=deroi').catch(() => ({ data: { data: [] } })),
      // The clock (mig 298) — one read for the whole list, so the table and the nightly
      // alert are quoting the same definition of late rather than each deriving its own.
      api.get('/jobwork/tat').catch(() => ({ data: { data: [] } })),
    ]);
    setJwos(j.data.data || []); setGrns(g.data.data || []); setInvoices(i.data.data || []);
    setGroups({ rsr: gr.data.data || [], deroi: gd.data.data || [] });
    setTat(Object.fromEntries((t.data.data || []).map(r => [r.jwo_id, r])));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/suppliers').then(r => setSuppliers((r.data.data || []).filter(s => s.is_active !== false))).catch(() => {});
    api.get('/procurement/deroi-vendors?active=true&approval=approved').then(r => setDeroiVendors(r.data.data || [])).catch(() => {});
    api.get('/oe/gpos').then(r => setGpos((r.data.data || []).filter(g => !g.is_superseded))).catch(() => {});
  }, []);

  const setStatus = async (jw, action) => {
    const body = { action };
    // Delivered captures the REAL arrival date (mig 137), never today-by-default.
    // Prompt is deliberately blank: a prefilled "today" is what produced the
    // click-stamped dates this replaces.
    if (action === 'delivered') {
      const entered = window.prompt(
        `${jw.jwo_no} — what date did the goods physically ARRIVE?\n\n`
        + `Enter YYYY-MM-DD. This is the vendor's delivery date, not today's date.\n`
        + (jw.collection_date ? `Collected ${fmtD(jw.collection_date)}.` : ''), '');
      if (entered === null) return;                       // cancelled
      if (!entered.trim()) { alert('The arrival date is required.'); return; }
      body.actual_delivery_date = entered.trim();
    }
    // Collected gets the same treatment. The server stamped CURRENT_DATE here, which
    // recorded the click and not the pick-up — the same defect mig 137 removed from
    // 'delivered' one branch above. RSR-only: Deroi derives 'collected' from its issue
    // challan and is refused this path.
    if (action === 'collected') {
      const entered = window.prompt(
        `${jw.jwo_no} — what date did the vendor physically COLLECT the material?\n\n`
        + `Enter YYYY-MM-DD. This is the pick-up date, not today's date.`, '');
      if (entered === null) return;                       // cancelled
      if (!entered.trim()) { alert('The collection date is required.'); return; }
      body.collection_date = entered.trim();
    }
    // A CANCELLATION MUST SAY WHY. Collected, processing and delivered each describe a thing
    // that happened and explain themselves; cancelled describes a thing that did NOT, and with
    // no reason the row is indistinguishable from work quietly abandoned. The server refuses
    // without one (400 CANCEL_REASON_REQUIRED) — this asks for it rather than letting the
    // click fail. Blank prompt on purpose: a prefilled reason is nobody's reason.
    if (action === 'cancelled') {
      const why = window.prompt(
        `${jw.jwo_no} — cancel this job work order.

`
        + `${jw.process_type} · ${Number(jw.qty_sent || 0).toLocaleString('en-IN')} ${jw.unit || ''} · ${jw.vendor_name || ''}

`
        + `Why? Say what happened to the work — or which JWO supersedes this one.
`
        + `This is recorded permanently against the order and cannot be edited afterwards.`, '');
      if (why === null) return;                           // dismissed
      if (!why.trim()) { alert('A reason is required to cancel.'); return; }
      body.reason = why.trim();
      if (!window.confirm(`Cancel ${jw.jwo_no}?

Reason: ${why.trim()}

This cannot be undone.`)) return;
    }
    try { await api.patch(`/jobwork/${jw.id}/status`, body); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  // RE-DERIVE A TYPED GRN FROM ITS LOTS (mig 416; Rajan, 26 Oct 2026). Admin/management only, once, with a reason.
  // The typed figure, who, when and why stay on the GRN; the quality verdict is not touched. Refusals come back from the
  // server in words (no lots yet → record the receipt challan first; a live invoice → reject it first).
  const rederiveGrn = async (g) => {
    const why = window.prompt(
      `${g.grn_no} — re-derive from its lots.\n\n`
      + `Typed now: ${num(g.qty_received).toLocaleString('en-IN')} ${g.unit || ''}. It will be replaced by the weight of this order's lots, `
      + `and the typed figure kept on the record. The quality verdict is not changed. This is done once.\n\nWhy?`, '');
    if (why === null) return;
    if (why.trim().length < 5) { alert('Say why, in a few words. It is kept on the GRN.'); return; }
    try {
      const r = await api.post(`/jobwork/grn/${g.id}/derive-from-lots`, { reason: why.trim() });
      alert(`${g.grn_no} now reads ${num(r.data.data.qty_received).toLocaleString('en-IN')} ${g.unit || ''}, from ${r.data.lots} lot(s). `
        + `Typed ${num(r.data.data.typed_qty_was).toLocaleString('en-IN')} kept on the record.`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const handleLogout = async () => { await logout(); navigate('/login'); };

  const filtered = (type) => jwos.filter(j => j.type === type)
    .filter(j => !filters.gpo_no || (j.gpo_no || '').includes(filters.gpo_no))
    .filter(j => !filters.process_type || j.process_type === filters.process_type)
    .filter(j => !filters.vendor_id || j.vendor_id === filters.vendor_id)
    .filter(j => !filters.status || j.status === filters.status)
    .filter(j => !filters.from || new Date(j.created_at) >= new Date(filters.from))
    .filter(j => !filters.to || new Date(j.created_at) <= new Date(filters.to + 'T23:59:59'));

  // A RENDER FUNCTION, NOT A COMPONENT — see the note on Procurement's H. As a component this
  // remounted the whole table on every keystroke in the GPO filter above it, losing the caret AND
  // the scroll position and re-rendering every row. It closes over 18 bindings, so module scope
  // would have meant an 18-prop signature on 108 lines; calling it as a function removes the
  // element type entirely and changes nothing else.
  const renderJwoTable = (type) => {
    const rows = filtered(type);
    const procs = type === 'rsr' ? RSR_PROCESSES : DEROI_PROCESSES;
    return (
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input style={inputStyle} placeholder="GPO…" value={filters.gpo_no || ''} onChange={e => setFilters({ ...filters, gpo_no: e.target.value })} />
          <select style={inputStyle} value={filters.process_type || ''} onChange={e => setFilters({ ...filters, process_type: e.target.value })}>
            <option value="">All processes</option>{procs.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select style={inputStyle} value={filters.vendor_id || ''} onChange={e => setFilters({ ...filters, vendor_id: e.target.value })}>
            <option value="">All vendors</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <select style={inputStyle} value={filters.status || ''} onChange={e => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All statuses</option>{PIPELINE.concat('cancelled').map(s => <option key={s} value={s}>{STATUS_TAG[s].label}</option>)}</select>
          <input type="date" style={inputStyle} value={filters.from || ''} onChange={e => setFilters({ ...filters, from: e.target.value })} />
          <input type="date" style={inputStyle} value={filters.to || ''} onChange={e => setFilters({ ...filters, to: e.target.value })} />
          {Object.values(filters).some(Boolean) && <button onClick={() => setFilters({})} style={{ ...inputStyle, cursor: 'pointer' }}>Clear</button>}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {['JWO No', 'GPO', 'Process', 'Vendor', 'Material', `Qty (${type === 'rsr' ? 'PCS' : 'KG'})`, `Rate/${type === 'rsr' ? 'pc' : 'KG'}`, 'Amount', 'Collection', 'Expected', 'Status', 'Actions'].map(h => (
              <th key={h} style={{ padding: '9px 8px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No {type.toUpperCase()} job work orders yet.</td></tr>
              : (() => {
                // One-to-many: group batches by GPO + process, header row per group.
                const keyOf = (j) => `${j.gpo_no}|${j.process_type}`;
                const grouped = {}; const order = [];
                rows.forEach(j => { const k = keyOf(j); if (!grouped[k]) { grouped[k] = []; order.push(k); } grouped[k].push(j); });
                const GSTATUS = {
                  open: { bg: '#FFF8E6', c: '#9A6B00', label: 'Open' },
                  partial: { bg: '#FEF0E6', c: '#C2410C', label: 'Partial' },
                  fully_sent: { bg: '#EEEDF8', c: '#3B3A8F', label: 'Fully Sent' },
                  completed: { bg: '#EAF7EE', c: '#166534', label: 'Completed' },
                };
                return order.flatMap(k => {
                  const [gpo, proc] = k.split('|');
                  const batches = grouped[k];
                  const g = (groups[type] || []).find(x => x.gpo_no === gpo && x.process_type === proc) || {};
                  const sent = g.sent ?? batches.reduce((s, b) => s + num(b.qty_sent), 0);
                  const req = g.required;
                  const pct = req ? Math.min(100, sent / req * 100) : 0;
                  const gs = GSTATUS[g.status] || GSTATUS.partial;
                  const isCollapsed = !!collapsed[k];
                  const header = (
                    <tr key={'h' + k} style={{ background: '#f4f5fa', borderTop: '2px solid #e2e4f0' }}>
                      <td style={{ padding: '9px 8px', fontFamily: 'monospace', fontWeight: 800, color: '#E22D2D', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        onClick={() => setCollapsed(c => ({ ...c, [k]: !c[k] }))} title={isCollapsed ? 'Expand batches' : 'Collapse batches'}>
                        <span style={{ color: '#3B3A8F', marginRight: 6 }}>{isCollapsed ? '▸' : '▾'}</span>{gpo}</td>
                      <td colSpan={2} style={{ padding: '9px 8px', fontWeight: 800 }}>{PROC_LABEL[proc] || proc} <span style={{ color: '#888', fontWeight: 400, fontSize: 10 }}>· {batches.length} batch{batches.length > 1 ? 'es' : ''}</span></td>
                      <td colSpan={2} style={{ padding: '9px 8px', fontSize: 11 }}>
                        {req !== null && req !== undefined
                          ? <span>Required: <strong>{num(req).toLocaleString('en-IN')} {g.unit}</strong> <span style={{ color: '#888' }}>(from {g.source})</span></span>
                          : <span style={{ color: '#9A6B00', fontWeight: 700 }}>⚠️ No {g.source || 'PPC plan'} for this GPO — the quantity cannot be checked</span>}
                      </td>
                      <td style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 700 }}>Sent: {num(sent).toLocaleString('en-IN')}</td>
                      <td colSpan={2} style={{ padding: '9px 8px', fontWeight: 700, color: g.balance > 0 ? '#C2410C' : '#166534' }}>
                        {g.balance !== null && g.balance !== undefined ? `Balance: ${num(g.balance).toLocaleString('en-IN')}` : ''}</td>
                      <td colSpan={2} style={{ padding: '9px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 90, background: '#e2e4f0', borderRadius: 6, height: 9, overflow: 'hidden' }}>
                            <div style={{ width: pct + '%', height: '100%', background: pct >= 100 ? '#16a34a' : '#f59e0b' }} />
                          </div>
                          <span style={{ fontSize: 10, color: '#888' }}>{req ? Math.round(pct) + '%' : '—'}</span>
                        </div>
                      </td>
                      <td style={{ padding: '9px 8px' }}><span style={tag(gs)}>{gs.label}</span></td>
                      <td style={{ padding: '9px 8px' }}>
                        {canManageType(type) && g.status !== 'completed' && (g.balance === null || g.balance > 0) && (
                          <button onClick={() => { setNewLock({ type, gpo_no: gpo, process_type: proc }); setShowNew(true); }}
                            style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F' }}>＋ Add Batch</button>
                        )}
                      </td>
                    </tr>
                  );
                  const batchRows = batches.map((j, bi) => (
                <tr key={j.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '8px 8px 8px 22px', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#888', fontSize: 9, fontWeight: 600, marginRight: 6 }}>BATCH {bi + 1}</span>
                    <span onClick={() => setDetailId(j.id)} style={{ color: '#3B3A8F', cursor: 'pointer', textDecoration: 'underline' }} title="Open JWO detail">{j.jwo_no}</span>
                    {j.reference_file_url && <a href={fileHref(j.reference_file_url)} target="_blank" rel="noreferrer" title="Reference / design file" style={{ marginLeft: 4 }}>📎</a>}
                    {j.knp_no && <span style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 6, fontSize: 8, fontWeight: 800, background: '#EAF7EE', color: '#166534' }} title="Attached KNP">🧶 {j.knp_no}</span>}
                  </td>
                  <td style={{ padding: '8px', fontFamily: 'monospace', color: '#bbb' }}>{j.gpo_no || '—'}</td>
                  <td style={{ padding: '8px', color: '#bbb' }}>{PROC_LABEL[j.process_type] || j.process_type}</td>
                  <td style={{ padding: '8px' }}>{j.vendor_name || '—'}</td>
                  <td style={{ padding: '8px' }}>{j.material_type || '—'}{j.material_description ? <span style={{ color: '#888', fontSize: 10 }}> · {j.material_description}</span> : ''}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{num(j.qty_sent).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>₹{num(j.rate).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700 }}>₹{num(j.total_amount).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{fmtD(j.collection_date)}</td>
                  {/* Due, and whether the BALANCE is late — read off the challans (mig 298).
                      What was here measured the typed expected date and excluded 'delivered',
                      so an order received 20 days late stopped showing as late the moment
                      somebody recorded it. The clock keeps saying so. */}
                  <td style={{ padding: '8px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {tat[j.id]?.clock_due_date ? fmtD(tat[j.id].clock_due_date) : fmtD(j.expected_delivery_date)}
                    {tat[j.id]?.balance_days_late > 0 && (
                      <div style={{ color: '#A32D2D', fontSize: 9, fontWeight: 800 }}>
                        {num(tat[j.id].balance_qty).toLocaleString('en-IN')} out · {tat[j.id].balance_days_late}d late
                      </div>)}
                    {tat[j.id]?.tat_state === 'closed_late' && (
                      <div style={{ color: '#9A6B00', fontSize: 9, fontWeight: 800 }}>closed late</div>)}
                    {tat[j.id] && !tat[j.id].clock_due_date && (
                      <div style={{ color: '#888', fontSize: 9 }} title={TAT_WHY[tat[j.id].clock_basis] || ''}>no clock</div>)}
                  </td>
                  <td style={{ padding: '8px' }}><Pipeline status={effStatus(j)} /></td>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                    <JwoActions j={j} canManage={canManageType(j.type)} canReceive={canReceiveType(j.type)} isMgmt={isMgmt} setStatus={setStatus}
                      onRaiseGrn={setGrnJwo} onUploadInvoice={setInvJwo} onChanged={load} />
                  </td>
                </tr>
                  ));
                  return [header, ...(isCollapsed ? [] : batchRows)];
                });
              })()}
          </tbody>
        </table>
        <ListFooter shown={rows.length} total={jwos.filter(j => j.type === type).length} noun="JWOs" />
      </div>
    );
  };

  const tabs = [
    { key: 'rsr', label: '🏢 RSR Job Work' },
    { key: 'deroi', label: '🏭 Deroi Job Work' },
    { key: 'grn', label: '📥 GRN & Quality' },
    { key: 'invoices', label: '🧾 Invoices' },
    { key: 'material', label: '🧵 Material Challans & Register' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      

      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#1a1a2e' }}>🔧 Job Work</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>Process outsourcing — RSR (embroidery/printing/washing/special finishing, per piece) and Deroi (knitting/dyeing/finishing, per KG). JWO → collected → processing → delivered → Q&Q + GRN → invoice.</p>
        </div>
        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 14px', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {tabs.map(t => (
                <button key={t.key} onClick={() => { setTab(t.key); setFilters({}); }} style={{
                  padding: '13px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13,
                  fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? '#3B3A8F' : '#888',
                  borderBottom: tab === t.key ? '2px solid #3B3A8F' : '2px solid transparent', whiteSpace: 'nowrap' }}>{t.label}</button>
              ))}
            </div>
            {canManageTab(tab) && ['rsr', 'deroi'].includes(tab) && <button onClick={() => setShowNew(true)} style={btnPrimary}>+ New Job Work Order</button>}
          </div>
          <div style={{ padding: 18, overflowX: 'auto' }}>
            {tab === 'rsr' && renderJwoTable('rsr')}
            {tab === 'deroi' && renderJwoTable('deroi')}

            {tab === 'grn' && (
              <div>
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F' }}>
                  📥 Receipt is recorded by Store or Merchandising; the quality verdict is signed separately by QA on this tab. The invoice stays blocked until it is.
                </div>
                {/* Delivered JWOs awaiting GRN — raise directly from this tab.
                    STATUS ALONE IS NOT THE TEST. jw.status does not move on receipt; it advances
                    delivered -> grn_raised on the QUALITY VERDICT (mig 189), so "delivered" covers
                    both not-yet-received AND received-but-unjudged. This list filtered on status
                    alone and so invited a second GRN on an order that already had one —
                    JWO-RSR-1806-001, 728 of 728 received, still listed here as awaiting. */}
                {(() => {
                  const awaiting = jwos.filter(j => j.status === 'delivered' && !j.grn_no);
                  if (!awaiting.length) return null;
                  return (
                    <div style={{ marginBottom: 16, border: '1px solid #f6d195', borderRadius: 8, background: '#FFFDF5', padding: '10px 14px' }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#9A6B00', marginBottom: 8 }}>⏳ Delivered — awaiting GRN ({awaiting.length})</div>
                      {awaiting.map(j => (
                        <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5ecd9', fontSize: 12, flexWrap: 'wrap', gap: 6 }}>
                          <span><strong style={{ fontFamily: 'monospace', color: '#3B3A8F' }}>{j.jwo_no}</strong> · {PROC_LABEL[j.process_type] || j.process_type} · {j.vendor_name} · {num(j.qty_sent).toLocaleString('en-IN')} {j.unit} · delivered {fmtD(j.actual_delivery_date)}</span>
                          {canReceiveType(j.type) && <button onClick={() => setGrnJwo(j)} style={{ ...btnPrimary, padding: '4px 12px', fontSize: 11, background: '#166534' }}>📥 Raise GRN</button>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {/* RECEIVED, NOT JUDGED. Shown as its own group so it cannot be mistaken for
                    work still to be received. The verdict itself is signed in the GRN table
                    below on this same tab. */}
                {(() => {
                  const received = jwos.filter(j => j.status === 'delivered' && j.grn_no && !j.quality_status);
                  if (!received.length) return null;
                  return (
                    <div style={{ marginBottom: 16, border: '1px solid #cfcdf0', borderRadius: 8, background: '#F7F6FE', padding: '10px 14px' }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F', marginBottom: 8 }}>
                        ⏳ Received — awaiting a quality verdict ({received.length})
                      </div>
                      {received.map(j => (
                        <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e8e6f8', fontSize: 12, flexWrap: 'wrap', gap: 6 }}>
                          <span><strong style={{ fontFamily: 'monospace', color: '#3B3A8F' }}>{j.jwo_no}</strong> · {PROC_LABEL[j.process_type] || j.process_type} · {j.vendor_name} · {num(j.qty_received).toLocaleString('en-IN')} of {num(j.qty_sent).toLocaleString('en-IN')} {j.unit} received · <span style={{ fontFamily: 'monospace' }}>{j.grn_no}</span></span>
                          {/* ── WHERE THE VERDICT ACTUALLY LIVES DEPENDS ON THE BOOK (ruling 6) ──────
                              Deroi's verdict is per lot member in lot_member_qc, on Quality →
                              Greige QC. job_work_grn.quality_status is the surface that ruling
                              moved away from, and pointing at "the table below" sent people to
                              it. QA cannot act there anyway — GET /jobwork/grn is scoped by
                              jwScope, so a Deroi row is invisible to every QA account, all three
                              of which are RSR. */}
                          <span style={{ color: '#9A6B00', fontWeight: 700 }}>
                            {j.type === 'deroi'
                              ? 'greige verdict → Quality → 🧵 Greige QC (per lot member)'
                              : 'sign the verdict in the table below ↓'}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ background: '#F7F6F3' }}>
                    {['GRN No', 'Gate entry', 'JWO', 'GPO', 'Process', 'Vendor', 'Received', 'Qty Received', 'Shortage', 'Quality', 'Remarks', 'By', 'Signed by'].map(h => (
                      <th key={h} style={{ padding: '9px 8px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {grns.length === 0 ? <tr><td colSpan={13} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No job work GRNs yet.</td></tr>
                      : grns.map(g => (
                        <tr key={g.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 700, color: '#166534' }}>{g.grn_no}</td>
                          {/* Via the FK (mig 199) — g.* carries only the id, so the number is joined. */}
                          <td style={{ padding: '8px', fontFamily: 'monospace', color: g.gate_entry_no ? '#9A6B00' : '#ccc' }}>{g.gate_entry_no || '—'}</td>
                          <td style={{ padding: '8px', fontFamily: 'monospace', color: '#3B3A8F' }}>{g.jwo_no}</td>
                          <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{g.gpo_no || '—'}</td>
                          <td style={{ padding: '8px' }}>{PROC_LABEL[g.process_type] || g.process_type}</td>
                          <td style={{ padding: '8px' }}>{g.vendor_name}</td>
                          <td style={{ padding: '8px' }}>{fmtD(g.received_date)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{num(g.qty_received).toLocaleString('en-IN')} {g.unit}
                            {g.qty_basis === 'typed' && (
                              <div style={{ marginTop: 3 }}>
                                <span style={{ ...tag({ bg: '#FFF4E5', c: '#9A5B00' }), fontSize: 10 }} title={`Typed, not weighed: ${g.qty_reason || ''}`}>TYPED</span>
                                {isMgmt && <button onClick={() => rederiveGrn(g)} title="Replace the typed figure with the weight of this order's lots (mig 416)"
                                  style={{ marginLeft: 4, background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #cfcdf0', borderRadius: 5, padding: '2px 7px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Re-derive from lots</button>}
                              </div>)}
                            {g.typed_qty_was != null && (
                              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }} title={g.rederive_reason || ''}>was {num(g.typed_qty_was).toLocaleString('en-IN')} typed</div>)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: num(g.shortage) > 0 ? '#A32D2D' : '#166534' }}>{num(g.shortage) > 0 ? num(g.shortage) : '✓'}</td>
                          <td style={{ padding: '8px' }}>{g.quality_status
                            ? <span style={tag(Q_TAG[g.quality_status] || Q_TAG.conditional)}>{g.quality_status.toUpperCase()}</span>
                            : canSignQuality && String(g.received_by) === String(user?.id)
                              // The receiver may not sign, admins included (8 Oct) — said here so the
                              // button is not offered and then refused.
                              ? <span style={{ color: '#A32D2D', fontSize: 11 }} title="You recorded this receipt; someone else signs its quality.">you received this</span>
                            : canSignQuality
                              ? <button onClick={() => setQualityGrn(g)} style={{ background: '#2D2C72', color: '#fff', border: 0, borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✍ Sign quality</button>
                              : <span style={{ color: '#B45309', fontWeight: 700, fontSize: 11 }}>AWAITING QA</span>}</td>
                          <td style={{ padding: '8px', color: '#666' }}>{g.quality_remarks || '—'}</td>
                          <td style={{ padding: '8px' }}>{g.received_by_name || '—'}</td>
                          <td style={{ padding: '8px', color: g.quality_by_name ? '#166534' : '#aaa', fontWeight: g.quality_by_name ? 600 : 400 }}>{g.quality_by_name || 'pending'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <ListFooter shown={grns.length} total={grns.length} noun="GRNs" />
              </div>
            )}

            {tab === 'invoices' && (() => {
              // GRN-raised JWOs without an invoice yet — upload directly from this tab.
              // …and orders whose latest invoice was REJECTED: the corrected bill is a new entry.
              const awaitingInv = jwos.filter(j => (j.status === 'grn_raised' && !j.invoice_id)
                || (['grn_raised', 'invoiced'].includes(j.status) && j.invoice_review_status === 'rejected'));
              return (
              <div>
                {awaitingInv.length > 0 && (
                  <div style={{ marginBottom: 16, border: '1px solid #c7d2fe', borderRadius: 8, background: '#FBFBFE', padding: '10px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F', marginBottom: 8 }}>🧾 GRN raised — awaiting invoice ({awaitingInv.length})</div>
                    {awaitingInv.map(j => (
                      <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eef0fb', fontSize: 12, flexWrap: 'wrap', gap: 6 }}>
                        <span><strong style={{ fontFamily: 'monospace', color: '#3B3A8F' }}>{j.jwo_no}</strong> · {PROC_LABEL[j.process_type] || j.process_type} · {j.vendor_name} · JWO value ₹{num(j.total_amount).toLocaleString('en-IN')} · GRN {j.grn_no}</span>
                        {canManageType(j.type) && <button onClick={() => setInvJwo(j)} style={{ ...btnPrimary, padding: '4px 12px', fontSize: 11 }}>{j.invoice_review_status === 'rejected' ? `🧾 Enter corrected invoice (${j.invoice_no} rejected)` : '🧾 Upload Invoice'}</button>}
                      </div>
                    ))}
                  </div>
                )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: '#F7F6F3' }}>
                  {['JWO', 'GPO', 'Process', 'Vendor', 'Invoice No', 'Date', 'JWO Value', 'Taxable + GST', 'File', 'Certification', ''].map(h => (
                    <th key={h} style={{ padding: '9px 8px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {invoices.length === 0 ? <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No invoices uploaded yet.</td></tr>
                    : invoices.map(i => (
                      <tr key={i.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '8px', fontFamily: 'monospace', color: '#3B3A8F', fontWeight: 700 }}>{i.jwo_no}</td>
                        <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{i.gpo_no || '—'}</td>
                        <td style={{ padding: '8px' }}>{PROC_LABEL[i.process_type] || i.process_type}</td>
                        <td style={{ padding: '8px' }}>{i.vendor_name}</td>
                        <td style={{ padding: '8px', fontFamily: 'monospace' }}>{i.invoice_no}</td>
                        <td style={{ padding: '8px' }}>{fmtD(i.invoice_date)}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>₹{num(i.jwo_amount).toLocaleString('en-IN')}</td>
                        {/* mig 413: invoice_amount is the TAXABLE value; the GST read off the bill is shown beside it, with the
                            split the two GSTINs gave. An invoice entered before 413 says it has no GST recorded. */}
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          <div style={{ fontWeight: 700 }}>₹{num(i.invoice_amount).toLocaleString('en-IN')}</div>
                          {i.gst_rate_pct == null
                            ? <div style={{ fontSize: 10, color: '#999' }}>no GST recorded
                                {i.review_status === 'pending' && canManageType(i.jwo_type) &&
                                  <div><button onClick={() => setGstFor(i)} style={{ ...btnPrimary, padding: '2px 8px', fontSize: 10, marginTop: 3 }}>+ Record GST from the bill</button></div>}
                              </div>
                            : <div style={{ fontSize: 10, color: '#555', whiteSpace: 'nowrap' }}>
                                + GST {num(i.gst_rate_pct)}% ₹{num(i.gst_amount).toLocaleString('en-IN')}{i.gst_split ? ` (${i.gst_split === 'igst' ? 'IGST' : 'CGST+SGST'})` : ''} · SAC {i.sac_code}
                                <div style={{ fontWeight: 700, color: '#2D2C72' }}>= ₹{(num(i.invoice_amount) + num(i.gst_amount)).toLocaleString('en-IN')}</div>
                                {i.invoice_amount_claimed != null && Math.abs(num(i.invoice_amount_claimed) - num(i.invoice_amount) - num(i.gst_amount)) >= 0.005 &&
                                  <div style={{ color: '#A32D2D' }}>billed ₹{num(i.invoice_amount_claimed).toLocaleString('en-IN')}</div>}
                              </div>}
                        </td>
                        <td style={{ padding: '8px' }}>{i.file_url ? <a href={fileHref(i.file_url)} target="_blank" rel="noreferrer" style={{ color: '#3B3A8F', fontWeight: 700, fontSize: 11 }}>👁 View</a> : '—'}</td>
                        {/* CERTIFICATION is the decision (15 Oct). The row says who decided, when and why;
                            the old status column only says whether it was marked paid. */}
                        <td style={{ padding: '8px', maxWidth: 260 }}>
                          <span style={tag(i.review_status === 'approved' ? { bg: '#EAF7EE', c: '#166534' } : i.review_status === 'rejected' ? { bg: '#FCEBEB', c: '#A32D2D' } : { bg: '#FFF8E6', c: '#9A6B00' })}>
                            {i.review_status === 'pending' ? 'AWAITING CERTIFICATION' : i.review_status.toUpperCase()}</span>
                          {i.status === 'paid' && <span style={{ ...tag({ bg: '#EEEDF8', c: '#3B3A8F' }), marginLeft: 4 }}>PAID</span>}
                          {i.certified_at && <div style={{ fontSize: 10, color: '#666', marginTop: 3 }}>by {i.certified_by_name || '—'}, {fmtDLong(i.certified_at)}</div>}
                          {i.certify_remarks && <div style={{ fontSize: 10, color: i.review_status === 'rejected' ? '#A32D2D' : '#666', marginTop: 2, whiteSpace: 'normal' }}>“{i.certify_remarks}”</div>}
                        </td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                          {i.can_certify && rejectFor !== i.id && <>
                            <button onClick={() => certify(i, 'approved')} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: '#166534' }}>✓ Approve</button>
                            <button onClick={() => { setRejectFor(i.id); setRejectWhy(''); }} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: '#A32D2D', marginLeft: 6 }}>✗ Reject</button>
                          </>}
                          {i.can_certify && rejectFor === i.id && (
                            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                              <input autoFocus value={rejectWhy} onChange={e => setRejectWhy(e.target.value)} placeholder="Reason for rejecting — the vendor is told"
                                style={{ ...inputStyle, width: 220, fontSize: 11 }} />
                              <button disabled={!rejectWhy.trim()} title={rejectWhy.trim() ? '' : 'A rejection needs a reason'}
                                onClick={() => certify(i, 'rejected', rejectWhy.trim())}
                                style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: rejectWhy.trim() ? '#A32D2D' : '#ccc', cursor: rejectWhy.trim() ? 'pointer' : 'not-allowed' }}>Reject</button>
                              <button onClick={() => { setRejectFor(null); setRejectWhy(''); }} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: '#f0f0f0', color: '#555' }}>Cancel</button>
                            </span>)}
                          {!i.can_certify && i.review_status === 'pending' && i.cannot_certify_reason &&
                            <span style={{ fontSize: 10, color: '#888', whiteSpace: 'normal', display: 'inline-block', maxWidth: 240 }}>{i.cannot_certify_reason}</span>}
                          {isMgmt && i.review_status === 'approved' && i.status !== 'paid' && <button onClick={async () => { await api.patch(`/jobwork/invoices/${i.id}/approve`, { status: 'paid' }).catch(e => alert(e.response?.data?.error || 'Failed')); load(); }} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10 }}>₹ Mark Paid</button>}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <ListFooter shown={invoices.length} total={invoices.length} noun="invoices" />
              </div>
              );
            })()}

            {tab === 'material' && <JobWorkChallans lockedCompany={materialLock} />}
          </div>
        </div>
      </div>

      {showNew && <NewJwoModal defaultType={tab === 'deroi' ? 'deroi' : 'rsr'} suppliers={suppliers} deroiVendors={deroiVendors} gpos={gpos} lock={newLock}
        onClose={() => { setShowNew(false); setNewLock(null); }} onSaved={() => { setShowNew(false); setNewLock(null); load(); }} />}
      {qualityGrn && <QualityModal grn={qualityGrn} onClose={() => setQualityGrn(null)} onSaved={() => { setQualityGrn(null); load(); }} />}
      {grnJwo && <GrnModal jw={grnJwo} onClose={() => setGrnJwo(null)} onSaved={() => { setGrnJwo(null); load(); }} />}
      {invJwo && <InvoiceModal jw={invJwo} onClose={() => setInvJwo(null)} onSaved={() => { setInvJwo(null); load(); }} />}
      {gstFor && <GstLaterModal inv={gstFor} onClose={() => setGstFor(null)} onSaved={() => { setGstFor(null); load(); }} />}
      {detailId && <JwoDetailModal id={detailId} jwos={jwos} canManage={canManageType((jwos.find(x => x.id === detailId) || {}).type)} canReceive={canReceiveType((jwos.find(x => x.id === detailId) || {}).type)} isMgmt={isMgmt} setStatus={setStatus}
        onRaiseGrn={(j) => { setDetailId(null); setGrnJwo(j); }} onUploadInvoice={(j) => { setDetailId(null); setInvJwo(j); }}
        onChanged={load} onClose={() => setDetailId(null)} />}
    </div>
  );
}

// ── JWO detail modal — full details, labeled pipeline, history, all actions ──
// ─── JWO PRINT ────────────────────────────────────────────────────────────────
// The job-work order had no print at all: no /pdf route on the backend, no handler
// here. This uses printDocument (utils/print.js), the same primitive the challan
// print in this module already uses — not renderLetterPdf. Two reasons: it keeps
// the module internally consistent, and printDocument has a REAL table primitive
// with headers and borders, where renderLetterPdf has none and fakes line items as
// repeated two-column rows. It also renders ₹ natively in the browser, so the
// embedded-font problem that mig-era PDFs had does not arise here.
//
// `j` must be the DETAIL payload from GET /jobwork/:id — the list row does not
// carry the party_* fields.
function printJwo(j) {
  const isDeroi = (j.type || '').toLowerCase() === 'deroi';
  const perUnit = j.unit === 'PCS' ? 'pc' : 'KG';

  // PARTY BLOCK — resolved server-side from vendor_id / deroi_vendor_id, which
  // mig 177's jwo_vendor_xor guarantees is exactly one of the two. Never typed,
  // and never read from the denormalised vendor_name: that column is display-only
  // and can lag the register it was copied from.
  //
  // The address is a single free-text blob with inconsistent formatting across
  // both registers, so it prints as one wrapped line rather than a structured
  // block. That is the data's shape, not a rendering fault.
  const party = [
    ['Name', j.party_name || j.vendor_name],
    ['Address', j.party_address],
    ['GSTIN', j.party_gstin],
    ['Phone', j.party_phone],
    ...(j.party_contact_name ? [['Contact person', j.party_contact_name]] : []),
    ...(j.party_email ? [['Email', j.party_email]] : []),
    ['Register', j.party_register === 'deroi_vendors' ? 'Deroi Vendors'
      : j.party_register === 'suppliers' ? 'Suppliers (RSR)' : '—'],
  ];

  // LINE ITEMS as a real bordered table. A JWO carries exactly ONE material line
  // by schema — there is no job_work_order_items table; the material lives on the
  // header (material_type / material_description / qty_sent / rate). So this table
  // has one row. It is still a table, not key-value pairs, because that is what
  // the document is: an order for a quantity of a material at a rate.
  const lineRows = [[
    1,
    j.material_type || '—',
    j.material_description || '—',
    PROC_LABEL[j.process_type] || j.process_type || '—',
    j.unit || '—',
    num(j.qty_sent).toLocaleString('en-IN'),
    inr(j.rate) + ' / ' + perUnit,
    inr(num(j.qty_sent) * num(j.rate)),
  ]];

  const sections = [
    { title: isDeroi ? 'Job Worker — Deroi Vendor Register' : 'Job Worker — Supplier Register', fields: party },
    { title: 'Order Details', fields: [
      ['JWO No', j.jwo_no],
      ['Process', PROC_LABEL[j.process_type] || j.process_type],
      ['GPO No', j.gpo_no],
      ['Material', `${j.material_type || '—'}${j.material_description ? ' · ' + j.material_description : ''}`],
      ['Qty Sent', `${num(j.qty_sent).toLocaleString('en-IN')} ${j.unit || ''}`.trim()],
      ['Rate', `${inr(j.rate)} per ${perUnit}`],
      ['Total Value', inr(j.total_amount)],
      ['Collection Date', fmtDLong(j.collection_date)],
      ['Expected Delivery', fmtDLong(j.expected_delivery_date)],
      ['Actual Delivery', fmtDLong(j.actual_delivery_date)],
      ...(j.knp_no ? [['KNP', j.knp_no]] : []),
    ] },
    { title: 'Line Items', table: {
      columns: ['#', 'Material', 'Description', 'Process', 'Unit', 'Qty', 'Rate', 'Value'],
      rows: lineRows,
    } },
  ];

  if (j.special_instructions) {
    sections.push({ title: 'Special Instructions', fields: [['Instructions', j.special_instructions]] });
  }
  sections.push({ fields: [
    ['', ''],
    ['For ' + (isDeroi ? 'Deroi Mart Pvt Ltd' : 'RSR International'), '\n\n______________________'],
    ['Accepted by (Job Worker)', '\n\n______________________'],
  ] });

  printDocument({
    docTitle: 'Job Work Order',
    subtitle: `${j.jwo_no} · ${(j.type || '').toUpperCase()} Job Work`,
    meta: [
      ['Status', (j.status || '').toUpperCase()],
      ['GPO', j.gpo_no || '—'],
      ['Expected', fmtDLong(j.expected_delivery_date)],
    ],
    // Letterhead by company — a Deroi JWO must not go out on RSR's letterhead.
    org: isDeroi ? 'deroi' : 'rsr',
    sections,
  });
}

// §F — negotiated delivery date + actual arrival date. Two PATCH endpoints that
// existed with no frontend caller at all. Kept as one small component so the
// detail modal stays readable; each posts only when both its date and its reason
// are present, because the server requires the reason and a silent 400 here would
// read as "the button does nothing".
function JwoDateEdits({ j, onChanged }) {
  const [open, setOpen] = React.useState(null);          // 'negotiated' | 'delivery' | null
  const [date, setDate] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const start = (which) => {
    setErr(''); setOpen(which); setReason('');
    setDate(String((which === 'negotiated' ? j.expected_delivery_date : j.actual_delivery_date) || '').slice(0, 10));
  };
  const save = async () => {
    if (!date) { setErr('Pick a date.'); return; }
    if (!reason.trim()) { setErr('A reason is required — this date is what lateness and debits are measured against.'); return; }
    setBusy(true); setErr('');
    try {
      if (open === 'negotiated') await api.patch(`/jobwork/${j.id}/negotiated-date`, { expected_delivery_date: date, reason });
      else await api.patch(`/jobwork/${j.id}/delivery-date`, { actual_delivery_date: date, reason });
      setOpen(null); onChanged();
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const btn = { padding: '4px 10px', fontSize: 11, borderRadius: 5, border: '1px solid #ddd', background: '#f0f2f8', color: '#3B3A8F', cursor: 'pointer', fontWeight: 600 };
  const inp = { padding: '6px 8px', border: '1px solid #ddd', borderRadius: 5, fontSize: 12 };
  return (
    <div style={{ margin: '0 0 12px', padding: '10px 14px', border: '1px solid #EEEDF8', borderRadius: 8, background: '#FBFBFE' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F' }}>DATES</span>
        <button onClick={() => start('negotiated')} style={btn}>📅 Re-negotiate delivery date</button>
        {/* delivery-date CORRECTS an arrival already recorded — the server refuses it
            until the JWO has one, so the button is hidden rather than offered-then-409. */}
        {j.actual_delivery_date && <button onClick={() => start('delivery')} style={btn}>📦 Correct arrival date</button>}
      </div>
      {open && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, width: 150 }} />
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required)…" style={{ ...inp, flex: 1, minWidth: 220 }} />
          <button disabled={busy} onClick={save} style={{ ...btn, background: '#3B3A8F', color: '#fff', border: 'none' }}>{busy ? '…' : 'Save'}</button>
          <button onClick={() => { setOpen(null); setErr(''); }} style={btn}>Cancel</button>
        </div>
      )}
      {open === 'negotiated' && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#854F0B' }}>
          Re-measured against the benchmark snapshotted on this JWO — a date beyond the gate routes back to management for approval.
        </div>
      )}
      {err && <div style={{ marginTop: 6, fontSize: 12, color: '#A32D2D' }}>{err}</div>}
    </div>
  );
}

// ── THE TAT CLOCK (mig 298) ───────────────────────────────────────────────────────────────────
// Deliberately says why an order has no clock instead of rendering a blank or a zero. "Not dated"
// is a real state with two real reasons, and both are things somebody may need to act on.
const TAT_STATE = {
  open:          { label: 'Running',            bg: '#EEEDF8', fg: '#3B3A8F' },
  late:          { label: 'Overdue',            bg: '#FCEBEB', fg: '#A32D2D' },
  closed:        { label: 'Closed on time',     bg: '#EAF6EE', fg: '#1B7A46' },
  closed_late:   { label: 'Closed late',        bg: '#FFF4E5', fg: '#9A6B00' },
  awaiting_issue:{ label: 'Nothing issued yet', bg: '#F2F2F2', fg: '#666' },
  not_dated:     { label: 'No clock',           bg: '#F2F2F2', fg: '#666' },
};
const TAT_WHY = {
  legacy: 'Raised before the clock existed, so it is deliberately not dated — its issue and receipt '
        + 'were recorded on the same day and a computed turnaround would state an on-time record '
        + 'nobody stands behind.',
  issue_predates_order: 'Its issue challan is dated BEFORE the order was raised, which cannot be true. '
        + 'The clock refuses to date from it rather than measure against a date that is wrong by a month.',
  awaiting_issue: 'No issue challan yet — nothing has left, so nothing can be late.',
};

function TatPanel({ tat, receipts, unit }) {
  const s = TAT_STATE[tat.tat_state] || TAT_STATE.not_dated;
  const why = tat.tat_state === 'not_dated' || tat.tat_state === 'awaiting_issue' ? TAT_WHY[tat.clock_basis] : null;
  const q = (v) => `${num(v).toLocaleString('en-IN')} ${unit || ''}`.trim();
  return (
    <div style={{ margin: '12px 0', border: '1px solid #EEEDF8', borderRadius: 8, padding: '10px 14px', background: '#FBFBFE' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: why ? 6 : 8 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1 }}>⏱ Turnaround</span>
        <span style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>{s.label}</span>
        {tat.balance_days_late > 0 && (
          <span style={{ color: '#A32D2D', fontSize: 12, fontWeight: 700 }}>
            {q(tat.balance_qty)} still out, {tat.balance_days_late} day{tat.balance_days_late === 1 ? '' : 's'} past due
          </span>)}
      </div>
      {why
        ? <div style={{ fontSize: 11, color: '#666', lineHeight: 1.5 }}>{why}</div>
        : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '4px 14px', fontSize: 12 }}>
            <div><span style={{ color: '#888' }}>Issued: </span><strong>{fmtD(tat.clock_start_date)}</strong></div>
            <div><span style={{ color: '#888' }}>Due: </span><strong>{fmtD(tat.clock_due_date)}</strong>
              <span style={{ color: '#888' }}> ({num(tat.benchmark_turnaround_days)}d)</span></div>
            <div><span style={{ color: '#888' }}>Out: </span><strong>{q(tat.issued_qty)}</strong></div>
            <div><span style={{ color: '#888' }}>Back: </span><strong>{q(tat.received_qty)}</strong></div>
            <div><span style={{ color: '#888' }}>Balance: </span><strong style={{ color: num(tat.balance_qty) > 0 ? '#A32D2D' : '#1B7A46' }}>{q(tat.balance_qty)}</strong></div>
          </div>}
      {/* Per receipt, because a debit is owed per quantity that came back late — an order can be
          half on time. This is the debitable fact, and it sits on the document that recorded the
          arrival rather than on a date typed at a "delivered" click. */}
      {receipts.length > 0 && (
        <div style={{ marginTop: 8, borderTop: '1px solid #EEEDF8', paddingTop: 6 }}>
          {receipts.map(r => (
            <div key={r.receipt_challan_id} style={{ fontSize: 11, display: 'flex', gap: 8, flexWrap: 'wrap', padding: '2px 0' }}>
              <span style={{ fontFamily: 'monospace', color: '#3B3A8F' }}>{r.challan_no}</span>
              <span>{fmtD(r.received_on)}</span>
              <span>{q(r.qty_received)}</span>
              {r.days_late > 0
                ? <strong style={{ color: '#A32D2D' }}>{r.days_late} day{r.days_late === 1 ? '' : 's'} late</strong>
                : r.days_late === 0 || r.days_late === null
                  ? <span style={{ color: tat.clock_due_date ? '#1B7A46' : '#888' }}>{tat.clock_due_date ? 'on time' : 'no clock to measure against'}</span>
                  : null}
            </div>))}
        </div>)}
    </div>
  );
}

function JwoDetailModal({ id, jwos, canManage, canReceive = false, isMgmt, setStatus, onRaiseGrn, onUploadInvoice, onChanged, onClose }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get('/jobwork/' + id).then(r => setD(r.data.data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);
  // Keep list-row data (invoice_status etc.) in sync for the action buttons.
  const listRow = jwos.find(x => x.id === id);
  if (!d) return null;
  const j = { ...d, ...(listRow || {}) };
  const kv = (k, v) => <div style={{ fontSize: 12 }}><span style={{ color: '#888' }}>{k}: </span><strong>{v ?? '—'}</strong></div>;
  const fmtDT = (x) => x ? new Date(x).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
  // History synthesised from lifecycle timestamps + GRN/invoice records.
  const history = [
    { at: d.created_at, label: `JWO raised by ${d.created_by_name || '—'}` },
    d.collection_date && !['pending'].includes(d.status) ? { at: d.collection_date, label: 'Material collected by vendor' } : null,
    d.actual_delivery_date ? { at: d.actual_delivery_date, label: 'Delivered back by vendor' } : null,
    ...(d.grns || []).map(g => ({ at: g.created_at, label: `GRN ${g.grn_no} raised — qty ${num(g.qty_received).toLocaleString('en-IN')} ${d.unit}, quality ${String(g.quality_status || '').toUpperCase()} (${g.received_by_name || '—'})` })),
    ...(d.invoices || []).flatMap(i => [
      { at: i.created_at, label: `Invoice ${i.invoice_no} uploaded — ₹${num(i.invoice_amount).toLocaleString('en-IN')}` },
      i.approved_at ? { at: i.approved_at, label: `Invoice ${i.invoice_no} ${i.status === 'paid' ? 'approved & paid' : i.status} (${i.approved_by_name || '—'})` } : null,
      i.certified_at ? { at: i.certified_at, label: `Invoice ${i.invoice_no} ${i.review_status === 'approved' ? 'certified' : 'rejected at certification'} by ${i.certified_by_name || '—'}${i.certify_remarks ? ` — “${i.certify_remarks}”` : ''}` } : null,
    ]),
  ].filter(Boolean).sort((a, b) => new Date(a.at) - new Date(b.at));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2050, overflowY: 'auto', padding: '24px 0' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 14, width: '92%', maxWidth: 720, margin: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: '#2D2C72', padding: '14px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: 1 }}>{d.type === 'rsr' ? 'RSR' : 'Deroi'} Job Work · {PROC_LABEL[d.process_type] || d.process_type}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'white', marginTop: 2 }}>{d.jwo_no}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Prints from `d` — the GET /jobwork/:id payload — never from the list
                row, which carries no party_* fields. */}
            <button onClick={() => printJwo(d)} style={{ ...printBtnStyle, background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)' }}>🖨 Print</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 22, cursor: 'pointer' }}>✕</button>
          </div>
        </div>
        <div style={{ padding: '18px 22px 22px' }}>
          <div style={{ marginBottom: 16, overflowX: 'auto' }}><Pipeline status={effStatus(j)} size="lg" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px 14px', marginBottom: 12 }}>
            {kv('GPO', d.gpo_no)}{kv('Vendor', d.vendor_name)}{kv('Material', `${d.material_type || '—'}${d.material_description ? ' · ' + d.material_description : ''}`)}
            {kv('Qty Sent', `${num(d.qty_sent).toLocaleString('en-IN')} ${d.unit}`)}{kv('Rate', `₹${num(d.rate).toLocaleString('en-IN')}/${d.unit === 'PCS' ? 'pc' : 'KG'}`)}{kv('Total', `₹${num(d.total_amount).toLocaleString('en-IN')}`)}
            {kv('Collection', fmtD(d.collection_date))}{kv('Expected Delivery', fmtD(d.expected_delivery_date))}{kv('Actual Delivery', fmtD(d.actual_delivery_date))}
            {d.knp_no && kv('KNP', d.knp_no)}
            {d.qty_received !== null && kv('Qty Received', `${num(d.qty_received).toLocaleString('en-IN')} ${d.unit}`)}
            {num(d.shortage) > 0 && kv('Shortage', `${num(d.shortage)} ${d.unit}`)}
          </div>
          {/* THE CLOCK (mig 298). Read off the challans, not off the two typed dates above it:
              it starts when material physically left, stops per quantity as material comes back,
              and whatever has not come back keeps ageing. An order with no clock says WHY rather
              than showing a zero. */}
          {d.tat && <TatPanel tat={d.tat} receipts={d.tat_receipts || []} unit={d.unit} />}
          {d.special_instructions && <div style={{ fontSize: 12, background: '#F7F6F3', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}><strong>Instructions:</strong> {d.special_instructions}</div>}
          {d.reference_file_url && <a href={fileHref(d.reference_file_url)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#3B3A8F', fontWeight: 700 }}>📎 Reference / design file</a>}

          <div style={{ margin: '14px 0 10px', padding: '12px 14px', border: '1px solid #EEEDF8', borderRadius: 8, background: '#FBFBFE', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F' }}>NEXT ACTION</span>
            <JwoActions j={j} canManage={canManage} canReceive={canReceive} isMgmt={isMgmt} setStatus={async (jw, a) => { await setStatus(jw, a); load(); }}
              onRaiseGrn={onRaiseGrn} onUploadInvoice={onUploadInvoice} onChanged={() => { onChanged(); load(); }} size="lg" />
          </div>

          {/* §F — the two DATA-EDIT endpoints. They change a date on the JWO; they are
              not approvals and do not belong in an approvals queue (the two that ARE
              approvals — date-decision and excess-decision — are inline in the
              Approvals hub instead). Both had no caller anywhere in the frontend.
              Management-only, mirroring authorize('admin','management') on the server;
              both demand a reason because these dates are what lateness and debits are
              measured against. */}
          {isMgmt && <JwoDateEdits j={d} onChanged={() => { onChanged(); load(); }} />}

          <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #EEEDF8', paddingBottom: 4, marginBottom: 8 }}>🕘 History</div>
          {history.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12, padding: '5px 0', borderBottom: '1px solid #f5f5f5' }}>
              <span style={{ color: '#888', whiteSpace: 'nowrap', minWidth: 120 }}>{fmtDT(h.at) || fmtD(h.at)}</span>
              <span>{h.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewJwoModal({ defaultType, suppliers, deroiVendors = [], gpos, lock, onClose, onSaved }) {
  const { user } = useAuth();   // the exception field is management's alone (mig 297)
  const [form, setForm] = useState({
    type: lock?.type || defaultType,
    material_type: (lock?.type || defaultType) === 'rsr' ? 'panels' : 'fabric',
    gpo_no: lock?.gpo_no || '', process_type: lock?.process_type || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [limitInfo, setLimitInfo] = useState(null);
  // ── THE GREIGE GSM, PER COMPONENT (ruling 23/25) ────────────────────────────────────────
  // Greige is heavier than finished, so it has its own target and Fabrics sets it HERE, when the
  // knitting order is raised, because it depends on what this run will lose at dyeing and
  // finishing. NO DEFAULT: a greige target nobody entered is not a greige target, and deriving
  // one from the finished figure would be the system inventing the number the inspector is meant
  // to judge against. Left blank, Greige QC says "not declared" and offers nothing to judge on.
  const [greigeRows, setGreigeRows] = useState([]);
  const [knps, setKnps] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const procs = form.type === 'rsr' ? RSR_PROCESSES : DEROI_PROCESSES;
  const isKnitting = form.type === 'deroi' && form.process_type === 'knitting';

  // TAT benchmark (mig 135/136) — suggests a delivery date and measures what the
  // merchant actually negotiated against it. Calendar days, matching the server.
  useEffect(() => { api.get('/jobwork/tat-benchmark').then(r => setBenchmarks(r.data.data || [])).catch(() => setBenchmarks([])); }, []);
  const bench = benchmarks.find(b => b.type === form.type && b.process_type === form.process_type);
  const tat = bench && {
    turnaround: Number(bench.turnaround_days), issue: Number(bench.issue_days),
  };
  const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + Math.round(n)); return d.toISOString().slice(0, 10); };
  const suggested = tat ? addDays(tat.issue + tat.turnaround) : null;
  const negTurnaround = tat && form.expected_delivery_date
    ? Math.round((new Date(form.expected_delivery_date) - new Date(new Date().toISOString().slice(0, 10))) / 86400000) - tat.issue
    : null;
  const excess = negTurnaround != null ? +(negTurnaround - tat.turnaround).toFixed(2) : null;
  const gate = tat ? Math.max(tat.turnaround + 7, tat.turnaround * 2) : null;
  const overGate = negTurnaround != null && negTurnaround > gate;

  // Approved KNPs for the GPO — attachable to knitting JWOs.
  useEffect(() => {
    setKnps([]);
    if (!isKnitting || !form.gpo_no) return;
    api.get(`/procurement/knp?gpo_no=${encodeURIComponent(form.gpo_no)}&status=approved`)
      .then(r => setKnps(r.data.data || [])).catch(() => {});
  }, [isKnitting, form.gpo_no]);
  // The components this GPO declares — one greige row each, for a Deroi knitting order only.
  useEffect(() => {
    setGreigeRows([]);
    if (!(form.type === 'deroi' && isKnitting && form.gpo_no)) return;
    api.get(`/procurement/gpo-components?gpo_no=${encodeURIComponent(form.gpo_no)}`)
      .then(r => setGreigeRows((r.data.data || [])
        .filter(d => d.component)
        .map(d => ({ gpo_no: d.gpo_no, component: d.component,
                     finished_gsm: d.gsm_target, finished_tol: d.gsm_tolerance,
                     greige_gsm_target: '', greige_gsm_tolerance: '' }))))
      .catch(() => setGreigeRows([]));
  }, [form.type, isKnitting, form.gpo_no]);

  const unit = form.type === 'rsr' ? 'PCS' : 'KG';
  const total = (parseFloat(form.qty_sent) || 0) * (parseFloat(form.rate) || 0);

  // Fetch the process qty limit whenever the source inputs are complete.
  useEffect(() => {
    setLimitInfo(null);
    if (!form.type || !form.process_type || !form.gpo_no) return;
    const qs = new URLSearchParams({ type: form.type, process_type: form.process_type, gpo_no: form.gpo_no });
    if (form.material_type) qs.set('material_type', form.material_type);
    api.get('/jobwork/limit?' + qs.toString()).then(r => setLimitInfo(r.data.data)).catch(() => {});
  }, [form.type, form.process_type, form.gpo_no, form.material_type]);

  const overLimit = limitInfo?.limit !== null && limitInfo?.limit !== undefined
    && (parseFloat(form.qty_sent) || 0) > (limitInfo.balance ?? Infinity) + 1e-6;

  // THE RATE CEILING (mig 297). The estimate for this fabric already costed this process, and the
  // rate may not exceed it. Fetched as soon as GPO and process are known, so the number is on the
  // screen BEFORE the rate is typed — a ceiling you only meet by being refused is a worse ceiling.
  // No default is picked when the GPO resolves to several estimates: every live Deroi GPO resolves
  // to two whose knitting figures are ₹10 and ₹35, and choosing between them is the user's call.
  const [caps, setCaps] = useState(null);
  useEffect(() => {
    setCaps(null);
    setForm(f => ({ ...f, estimate_id: '', cap_exception_reason: '' }));
    if (form.type !== 'deroi' || !form.gpo_no || !form.process_type) return;
    const qs = new URLSearchParams({ gpo_no: form.gpo_no, process_type: form.process_type });
    api.get('/jobwork/rate-cap?' + qs.toString()).then(r => {
      setCaps(r.data);
      if (r.data.default_id) setForm(f => ({ ...f, estimate_id: r.data.default_id }));
    }).catch(() => {});
  }, [form.type, form.gpo_no, form.process_type]);
  const estOptions = caps?.data || [];
  const picked = estOptions.find(e => e.id === form.estimate_id) || null;
  const overCap = !!picked && picked.cap != null && (parseFloat(form.rate) || 0) > picked.cap + 1e-6;
  const capBlocks = overCap && picked.status === 'approved';   // approved caps; a draft only warns

  const save = async () => {
    // OVER-COMMITMENT WARNS, IT DOES NOT BLOCK (2026-08-17). Until today the knitting limit
    // could never resolve — both source queries were structurally incapable of matching a
    // Deroi GPO — so overLimit was permanently false and this guard had never once fired.
    // Making it blocking the moment the limit starts working would refuse legacy rows that
    // are already over: JWO-DEROI-1803-001 commits 444 KG against 406.673 received. Refusing
    // a batch on a ceiling nobody has been able to see is how a correct check gets switched
    // off. It shows red, it says by how much, and it lets the user proceed.
    // NO CEILING AT ALL — confirm, do not silently proceed (mig 304). Refusing would send the
    // work around the screen; permitting silently is what put 1,035 kg on GPO 1737 against yarn
    // that was never bought.
    if (limitInfo && limitInfo.limit === null && form.type === 'deroi') {
      const why = limitInfo.unverified_reason || `there is no ${limitInfo.source} for this GPO`;
      if (!window.confirm(
        `${why}.\n\nThe quantity you are committing — ${form.qty_sent || '?'} KG — cannot be checked `
        + `against anything upstream.\n\nRaise it anyway? Your name and the time will be recorded on the order.`)) return;
      form.qty_unverified_ack = true;
    }
    if (overLimit && !window.confirm(
      `This exceeds the yarn received for this GPO.\n\n` +
      `Limit ${limitInfo.limit} ${limitInfo.unit} (from ${limitInfo.source}) · already committed ` +
      `${limitInfo.allocated} · balance ${limitInfo.balance} ${limitInfo.unit}.\n\nRaise it anyway?`)) return;
    const v = (form.type === 'deroi' ? deroiVendors : suppliers).find(s => s.id === form.vendor_id);
    if (v?.is_blacklisted) { setError('Vendor is blacklisted. Cannot raise JWO.'); return; }
    // A typed 0 is not a rate (3 Oct 2026): it raised JWO-DEROI-1742-004 at Rs 0/kg, whose receipt is
    // then refused for want of a rate. The server refuses it too (RATE_NOT_POSITIVE).
    if (!(parseFloat(form.rate) > 0)) { setError(`The rate must be more than ₹0 per ${unit === 'PCS' ? 'piece' : 'KG'}. A zero rate cannot be costed when the material comes back.`); return; }
    setBusy(true); setError('');
    // Only rows Fabrics actually filled travel. An empty row is not a zero target; it is the
    // absence the Greige QC screen reports as "not declared".
    const greige_targets = greigeRows
      .filter(g => String(g.greige_gsm_target || '').trim() !== '')
      .map(g => ({ gpo_no: g.gpo_no, component: g.component,
                   greige_gsm_target: Number(g.greige_gsm_target),
                   ...(String(g.greige_gsm_tolerance || '').trim() !== ''
                       ? { greige_gsm_tolerance: Number(g.greige_gsm_tolerance) } : {}) }));
    try { await api.post('/jobwork', { ...form, ...(greige_targets.length ? { greige_targets } : {}) }); onSaved(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to raise JWO'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 580, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>＋ New Job Work Order{lock ? ` — Batch for ${lock.gpo_no} · ${PROC_LABEL[lock.process_type]}` : ''}</h3></div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {greigeRows.length > 0 && (
          <div style={{ background: '#F7F6F3', border: '1px solid #e6e4de', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', marginBottom: 2 }}>🧵 Greige GSM target, per component</div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
              Set by Fabrics for this run. Greige is heavier than finished, so it is judged against its own
              figure — the finished GSM beside each row is context, not the target. Leave a row blank and
              Greige QC will say “not declared” rather than judging that cloth against the finished number.
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ color: '#888', fontSize: 10, textTransform: 'uppercase' }}>
                {['Component', 'Finished (context)', 'Greige GSM', 'Tolerance ±'].map(h =>
                  <th key={h} style={{ textAlign: 'left', padding: '3px 4px', fontWeight: 600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {greigeRows.map((g, i) => (
                  <tr key={`${g.gpo_no}-${g.component}`}>
                    <td style={{ padding: '3px 4px', fontWeight: 600 }}>{g.component}</td>
                    <td style={{ padding: '3px 4px', color: '#888' }}>
                      {g.finished_gsm == null ? 'not declared'
                        : `${g.finished_gsm}${g.finished_tol != null ? ` ± ${g.finished_tol}` : ''}`}
                    </td>
                    <td style={{ padding: '3px 4px', width: 110 }}>
                      <input type="number" step="0.01" placeholder="—"
                        value={g.greige_gsm_target}
                        onChange={e => setGreigeRows(rs => rs.map((x, j) => j === i ? { ...x, greige_gsm_target: e.target.value } : x))}
                        style={{ width: '100%', padding: '5px 7px', border: '1px solid #ddd', borderRadius: 5, fontSize: 12, boxSizing: 'border-box' }} />
                    </td>
                    <td style={{ padding: '3px 4px', width: 110 }}>
                      <input type="number" step="0.01" placeholder="—"
                        value={g.greige_gsm_tolerance}
                        onChange={e => setGreigeRows(rs => rs.map((x, j) => j === i ? { ...x, greige_gsm_tolerance: e.target.value } : x))}
                        style={{ width: '100%', padding: '5px 7px', border: '1px solid #ddd', borderRadius: 5, fontSize: 12, boxSizing: 'border-box' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {limitInfo && (limitInfo.limit !== null
          ? <div style={{ background: overLimit ? '#FCEBEB' : '#EEEDF8', color: overLimit ? '#A32D2D' : '#3B3A8F', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 600 }}>
              Available qty for {PROC_LABEL[form.process_type] || form.process_type}: <strong>{Number(limitInfo.limit).toLocaleString('en-IN')} {limitInfo.unit}</strong> (from {limitInfo.source})
              · Already allocated: {Number(limitInfo.allocated).toLocaleString('en-IN')} · Balance: <strong>{Number(limitInfo.balance).toLocaleString('en-IN')} {limitInfo.unit}</strong>
              {overLimit && <div style={{ marginTop: 4 }}>⚠️ Over-committed against yarn received — you will be asked to confirm. Not blocked while the limit is being proven.</div>}
            </div>
          : <div style={{ background: '#FFF8E6', color: '#9A6B00', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 600 }}>
              {/* It used to read "<source> data not available", which describes a broken lookup.
                  The lookup is fine; the GPO has nothing upstream. It was reported as a system
                  fault because it was worded as one. The reason comes from the API, where what
                  is missing is actually known. */}
              ⚠️ {limitInfo.unverified_reason || `No ${limitInfo.source} for this GPO`} — the quantity cannot be checked.
              {form.type === 'deroi' && <div style={{ fontWeight: 400, marginTop: 3 }}>
                You can still raise the order, but you will be asked to confirm, and the confirmation is recorded against your name.
              </div>}
            </div>)}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={labelStyle}>Type *</label>
            <select style={{ ...inputStyle, width: '100%', ...(lock ? { background: '#F7F6F3' } : {}) }} disabled={!!lock} value={form.type} onChange={e => setForm({ ...form, type: e.target.value, process_type: '', material_type: e.target.value === 'rsr' ? 'panels' : 'fabric', vendor_id: '' })}>
              <option value="rsr">RSR (per piece)</option><option value="deroi">Deroi (per KG)</option>
            </select></div>
          <div><label style={labelStyle}>GPO No *</label>
            <select style={{ ...inputStyle, width: '100%', ...(lock ? { background: '#F7F6F3' } : {}) }} disabled={!!lock} value={form.gpo_no || ''} onChange={e => setForm({ ...form, gpo_no: e.target.value })}>
              <option value="">— select —</option>{gpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no} · {g.style_no || ''}</option>)}
            </select></div>
          <div><label style={labelStyle}>Process Type *</label>
            <select style={{ ...inputStyle, width: '100%', ...(lock ? { background: '#F7F6F3' } : {}) }} disabled={!!lock} value={form.process_type || ''} onChange={e => setForm({ ...form, process_type: e.target.value })}>
              <option value="">— select —</option>{procs.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          <div><label style={labelStyle}>Vendor *</label>
            {(() => {
              // Vendor register follows the JWO's company: Deroi → deroi_vendors,
              // RSR → suppliers of type/category 'job_work' (fallback to all suppliers).
              let pool, emptyNote = null;
              if (form.type === 'deroi') {
                pool = deroiVendors.map(v => ({ ...v, name: v.vendor_name }));
                if (!pool.length) emptyNote = 'No Deroi vendors yet — add them in the Deroi module.';
              } else {
                const jwVendors = suppliers.filter(s => s.type === 'job_work' || s.category === 'job_work');
                pool = jwVendors.length ? jwVendors : suppliers;
                if (!jwVendors.length) emptyNote = 'No job_work-type vendors yet — showing all suppliers';
              }
              return (
                <>
                  <select style={{ ...inputStyle, width: '100%' }} value={form.vendor_id || ''} onChange={e => setForm({ ...form, vendor_id: e.target.value })}>
                    <option value="">— select —</option>{pool.map(s => <option key={s.id} value={s.id}>{s.name} ({s.category || s.type || '—'}){s.current_grade ? ` [${s.current_grade}]` : ''}{s.is_blacklisted ? ' ⛔ BLACKLISTED' : s.current_grade === 'D' ? ' ⚠ RISK' : s.current_grade === 'C' ? ' ⚠ WATCH' : ''}</option>)}
                  </select>
                  {(() => { const v = pool.find(s => s.id === form.vendor_id); if (!v) return null;
                    if (v.is_blacklisted) return <div style={{ marginTop: 4, padding: '5px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#991b1b' }}>⛔ Vendor is blacklisted. Cannot raise JWO.</div>;
                    if (v.current_grade === 'D') return <div style={{ marginTop: 4, padding: '5px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#991b1b' }}>⚠️ Blacklist Risk — management approval required</div>;
                    if (v.current_grade === 'C') return <div style={{ marginTop: 4, padding: '5px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>⚠️ Watch List vendor</div>;
                    return null; })()}
                  {emptyNote && <div style={{ fontSize: 9, color: '#9A6B00', marginTop: 2 }}>{emptyNote}</div>}
                </>
              );
            })()}</div>
          <div><label style={labelStyle}>Material Type</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.material_type || ''} onChange={e => setForm({ ...form, material_type: e.target.value })}>
              {(form.type === 'rsr' ? ['panels', 'garments'] : ['yarn', 'fabric']).map(m => <option key={m} value={m}>{m}</option>)}
            </select></div>
          {isKnitting && (
            <div><label style={labelStyle}>Attach KNP (approved)</label>
              <select style={{ ...inputStyle, width: '100%' }} value={form.knp_id || ''} onChange={e => setForm({ ...form, knp_id: e.target.value || undefined })}>
                <option value="">— none —</option>
                {knps.map(k => <option key={k.id} value={k.id}>{k.knp_no} · {k.fabric_master_name || k.fabric_description || ''} · {Number(k.greige_planned_kg ?? k.required_kg ?? 0).toLocaleString('en-IN')} KG greige</option>)}
              </select>
              {!knps.length && form.gpo_no && <div style={{ fontSize: 9, color: '#9A6B00', marginTop: 2 }}>No approved KNPs for this GPO yet</div>}
            </div>
          )}
          <div><label style={labelStyle}>Material Description</label><input style={{ ...inputStyle, width: '100%' }} value={form.material_description || ''} onChange={e => setForm({ ...form, material_description: e.target.value })} /></div>
          <div><label style={labelStyle}>Qty to Send ({unit}) *</label><input type="number" step="0.001" style={{ ...inputStyle, width: '100%' }} value={form.qty_sent || ''} onChange={e => setForm({ ...form, qty_sent: e.target.value })} /></div>
          <div><label style={labelStyle}>Rate per {unit === 'PCS' ? 'piece' : 'KG'} (₹) *</label><input type="number" step="0.01" style={{ ...inputStyle, width: '100%', ...(capBlocks ? { borderColor: '#A32D2D' } : {}) }} value={form.rate || ''} onChange={e => setForm({ ...form, rate: e.target.value })} /></div>
          {caps && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Cost estimate this rate is judged against {estOptions.length > 1 ? '*' : ''}</label>
              {estOptions.length === 0
                ? <div style={{ fontSize: 11, color: '#9A6B00', background: '#FFF8E6', border: '1px solid #F0E0B0', borderRadius: 6, padding: '6px 10px' }}>
                    {form.gpo_no} has no cost estimate linked to its fabric, so there is no ceiling to check this rate against. The order can still be raised; it will be recorded as having none.
                  </div>
                : <>
                    <select style={{ ...inputStyle, width: '100%' }} value={form.estimate_id || ''}
                      onChange={e => setForm({ ...form, estimate_id: e.target.value, cap_exception_reason: '' })}>
                      <option value="">— pick the estimate —</option>
                      {estOptions.map(e => (
                        <option key={e.id} value={e.id}>
                          {e.estimate_no || '(no number)'} · {e.fabric_name} · {e.status}
                          {e.cap != null ? ` · ₹${e.cap}/KG for ${e.group}` : ' · no priced line for this process'}
                        </option>))}
                    </select>
                    {estOptions.length > 1 && !form.estimate_id && (
                      <div style={{ fontSize: 11, color: '#9A6B00', marginTop: 3 }}>
                        This GPO is planned on {estOptions.length} estimates with different ceilings — pick the one this order is priced against.
                      </div>)}
                    {picked && picked.cap == null && (
                      <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                        {picked.estimate_no || picked.fabric_name} prices nothing under {picked.group}, so this order has no ceiling. Value addition is deliberately outside every cap.
                      </div>)}
                    {picked && picked.cap != null && (
                      <div style={{ fontSize: 12, marginTop: 5, padding: '7px 10px', borderRadius: 6, fontWeight: 600,
                        background: overCap ? '#FCEBEB' : '#EAF6EE', color: overCap ? '#A32D2D' : '#1B7A46' }}>
                        Ceiling <strong>₹{Number(picked.cap).toLocaleString('en-IN')}/KG</strong>
                        <span style={{ fontWeight: 400 }}> — {picked.lines.map(l => `${l.head.replace(/_/g, ' ')} ₹${l.cost}`).join(' + ')}</span>
                        {overCap && (picked.status === 'approved'
                          ? <div style={{ fontWeight: 400, marginTop: 3 }}>₹{form.rate}/KG is above an <strong>approved</strong> estimate. Reduce the rate, or record a management exception below.</div>
                          : <div style={{ fontWeight: 400, marginTop: 3 }}>₹{form.rate}/KG is above this estimate, but it is still a <strong>draft</strong> — this warns, it does not block. The order will record it.</div>)}
                      </div>)}
                    {capBlocks && ['admin', 'management'].includes(user?.role) && (
                      <div style={{ marginTop: 6 }}>
                        <label style={labelStyle}>Management exception — why this rate is above the approved estimate *</label>
                        <input style={{ ...inputStyle, width: '100%' }} value={form.cap_exception_reason || ''}
                          onChange={e => setForm({ ...form, cap_exception_reason: e.target.value })}
                          placeholder="Recorded on the order and in its audit trail" />
                      </div>)}
                  </>}
            </div>)}
          <div style={{ gridColumn: '1 / -1', background: '#F7F6F3', borderRadius: 8, padding: '8px 12px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: '#666' }}>Total Amount (auto)</span>
            <strong style={{ fontSize: 16, color: '#3B3A8F' }}>₹{total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong></div>
          <div><label style={labelStyle}>Collection Date</label><input type="date" style={{ ...inputStyle, width: '100%' }} value={form.collection_date || ''} onChange={e => setForm({ ...form, collection_date: e.target.value })} /></div>
          <div><label style={labelStyle}>Negotiated Delivery *</label>
            <input type="date" style={{ ...inputStyle, width: '100%' }} value={form.expected_delivery_date || ''} onChange={e => setForm({ ...form, expected_delivery_date: e.target.value })} />
            {tat && !form.expected_delivery_date && (
              <div style={{ fontSize: 11, marginTop: 4, color: '#5A6B8C' }}>
                Suggested <strong>{fmtD(suggested)}</strong> — {tat.issue}d issue + {tat.turnaround}d vendor benchmark
                <button type="button" onClick={() => setForm({ ...form, expected_delivery_date: suggested })}
                  style={{ marginLeft: 6, padding: '1px 7px', fontSize: 11, border: '1px solid #D6E2FA', background: '#F2F6FF', color: '#2D2C72', borderRadius: 4, cursor: 'pointer' }}>use</button>
              </div>)}
            {excess != null && excess <= 0 && <div style={{ fontSize: 11, marginTop: 4, color: '#1B7A46' }}>Within the {tat.turnaround}d benchmark.</div>}
            {excess > 0 && !overGate && (
              <div style={{ fontSize: 11, marginTop: 4, color: '#A36A00' }}>
                <strong>{excess} day{excess === 1 ? '' : 's'} beyond company benchmark</strong>; further delay subject to debit.
              </div>)}
            {overGate && (
              <div style={{ fontSize: 11, marginTop: 4, color: '#A32D2D' }}>
                <strong>{excess} days beyond benchmark — over the {gate}d gate.</strong> The JWO will still be created, then routed to management for approval.
              </div>)}
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Special Instructions</label><textarea style={{ ...inputStyle, width: '100%', height: 48, resize: 'vertical' }} value={form.special_instructions || ''} onChange={e => setForm({ ...form, special_instructions: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Reference File (design for embroidery/printing)</label>
            <FileBtn label={form.reference_file_url ? '✅ attached — replace' : '📎 Attach reference file'} onUploaded={(u) => setForm({ ...form, reference_file_url: u })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? 'Raising…' : '＋ Raise JWO & Notify'}</button>
        </div>
      </div>
    </div>
  );
}

function GrnModal({ jw, onClose, onSaved }) {
  const isDeroi = jw.type === 'deroi';
  const [form, setForm] = useState({ received_date: '', qty_received: jw.qty_sent, gate_entry_no: '', qty_reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // §B — for DEROI the received quantity is DERIVED as Σ lots.net_out_kg and shown
  // READ-ONLY with the contributing lots listed. It is not typed here and the
  // server discards any typed value: a typed figure alongside gram-exact lots is a
  // second source of truth for one fact, and the two will diverge.
  const [recon, setRecon] = useState(null);
  useEffect(() => {
    if (!isDeroi) return;
    api.get(`/jobwork/${jw.id}/reconciliation`).then(r => setRecon(r.data.data)).catch(() => setRecon({ error: true }));
  }, [isDeroi, jw.id]);
  const derived = recon && !recon.error ? recon.sum_net_out_kg : null;
  // Settled only once the reconciliation has answered — `recon == null` is "still asking",
  // not "no lots", and branching on it early would flash a typed field at someone whose
  // lots are about to load.
  const noLots = isDeroi && recon != null && !recon.error && recon.lots_count === 0;
  const effectiveQtyRecd = isDeroi ? derived : (parseFloat(form.qty_received) || 0);
  const shortage = +((num(jw.qty_sent)) - (effectiveQtyRecd || 0)).toFixed(3);
  const save = async () => {
    setError('');
    // The server owns these rules; these guards exist so the operator is told BEFORE a round
    // trip, and so the reason field is never silently dropped.
    if (recon == null && isDeroi) { setError('Still loading the lots for this JWO — give it a moment.'); return; }
    if (noLots) {
      if (!(parseFloat(form.qty_received) > 0)) {
        setError('Enter how much came back — there are no lots for this JWO to derive it from.'); return;
      }
      if (!form.qty_reason.trim()) {
        setError('Say why this cannot come from lots. It is stored on the receipt beside the quantity.'); return;
      }
    }
    setBusy(true);
    // gate_entry_no is stripped when blank rather than posted as '' — the resolver treats an
    // empty string as absent anyway, but sending nothing keeps the intent on the wire (mig 199).
    // qty_reason is stripped the same way when the quantity is derived: the CHECK in mig 292
    // refuses a reason alongside basis 'lots', so sending a stray '' would be a 500.
    const { gate_entry_no, qty_reason, ...rest } = form;
    const base = noLots ? { ...rest, qty_reason: qty_reason.trim() } : rest;
    const payload = gate_entry_no.trim() ? { ...base, gate_entry_no: gate_entry_no.trim() } : base;
    try { const r = await api.post(`/jobwork/${jw.id}/grn`, payload); onSaved(); alert(`✅ ${r.data.grn_no} raised.`); }
    catch (err) { setError(err.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 440, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>📥 Raise GRN — {jw.jwo_no}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888' }}>{PROC_LABEL[jw.process_type]} · {jw.vendor_name} · sent {num(jw.qty_sent).toLocaleString('en-IN')} {jw.unit}</p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {/* The gate register (mig 199). Optional — goods coming back from a job-work vendor
            must be receivable whether or not the clerk has the guard's register to hand. */}
        <div style={{ marginBottom: 14, padding: 12, background: '#FBF7EF', border: '1px solid #f6d195', borderRadius: 6 }}>
          <label style={{ ...labelStyle, color: '#9A6B00' }}>Gate entry number</label>
          <input style={{ ...inputStyle, width: '100%' }} value={form.gate_entry_no}
            onChange={e => setForm({ ...form, gate_entry_no: e.target.value })}
            placeholder="IN-entry number from the gate register" />
          <div style={{ fontSize: 11, color: '#9A6B00', marginTop: 4 }}>
            From the security guard's register — not {jw.jwo_no}, which is ours.
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={labelStyle}>Received Date</label><input type="date" style={{ ...inputStyle, width: '100%' }} value={form.received_date} onChange={e => setForm({ ...form, received_date: e.target.value })} /></div>
          {isDeroi && noLots ? (
            /* NO LOT CAN EXIST FOR THIS JWO (mig 292). Not "none recorded yet" — a lot comes
               only from a greige return against an OPEN yarn issue challan, and where no such
               challan exists the derived path is closed permanently. So the figure is typed,
               and the reason it had to be is stored on the receipt beside it. */
            <div><label style={labelStyle}>Qty Received ({jw.unit}) — typed *</label>
              <input type="number" step="0.001" style={{ ...inputStyle, width: '100%' }} value={form.qty_received}
                onChange={e => setForm(f => ({ ...f, qty_received: e.target.value }))} /></div>
          ) : isDeroi ? (
            <div><label style={labelStyle}>Qty Received ({jw.unit}) — derived</label>
              <div style={{ padding: '7px 9px', fontSize: 13, fontWeight: 800, color: derived != null ? '#3B3A8F' : '#A32D2D' }}>
                {recon == null ? 'loading lots…'
                  : recon.error ? 'could not load lots'
                  : `${derived.toLocaleString('en-IN')} ${jw.unit}`}
              </div></div>
          ) : (
            <div><label style={labelStyle}>Qty Received ({jw.unit}) *</label><input type="number" step="0.001" style={{ ...inputStyle, width: '100%' }} value={form.qty_received} onChange={e => setForm({ ...form, qty_received: e.target.value })} /></div>
          )}
          <div><label style={labelStyle}>Shortage (auto)</label>
            <div style={{ padding: '7px 9px', fontSize: 13, fontWeight: 800, color: shortage > 0 ? '#A32D2D' : shortage < 0 ? '#0891b2' : '#166534' }}>
              {shortage > 0 ? `${shortage} ${jw.unit} short` : shortage < 0 ? `${Math.abs(shortage)} excess` : 'Exact ✓'}</div></div>
          {noLots && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Why can this not come from lots? *</label>
              <input style={{ ...inputStyle, width: '100%' }} value={form.qty_reason}
                onChange={e => setForm(f => ({ ...f, qty_reason: e.target.value }))}
                placeholder="e.g. yarn for this GPO was issued before the challan flow existed — no open challan to return against" />
              <div style={{ fontSize: 11, color: '#7A5A00', marginTop: 4 }}>
                Stored on the receipt and shown wherever this quantity is. Shortage — and any debit it drives —
                is computed from the figure you type, so it is recorded who asserted it.
              </div>
            </div>
          )}
          <div style={{ gridColumn: '1 / -1', background: '#FFF8E7', border: '1px solid #F0E0B0', borderRadius: 6, padding: '8px 10px', fontSize: 11.5, color: '#7A5A00' }}>
            This records <strong>receipt only</strong>. Quality is signed separately by QA on the GRN &amp; Quality tab, and the invoice stays blocked until they do.
          </div>
          {/* The lots this figure is made of. Without them the derived number is
              just an assertion; with them the person raising the GRN can see the
              batches it came from. */}
          {isDeroi && recon && !recon.error && (
            <div style={{ gridColumn: '1 / -1', border: '1px solid #EEEDF8', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: '#F5F4FF', padding: '6px 10px', fontSize: 11, fontWeight: 800, color: '#2D2C72' }}>
                Contributing lots ({recon.lots_count}) · Σ net out {num(recon.sum_net_out_kg).toLocaleString('en-IN')} {jw.unit}
              </div>
              {recon.lots_count === 0
                ? <div style={{ padding: 10, fontSize: 12, color: '#A32D2D' }}>No lots carry a net output for this JWO yet — the GRN cannot be raised until at least one does.</div>
                : (<>
                  <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                    {recon.lots.map(l => (
                      <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4px 10px', fontSize: 11, borderTop: '1px solid #f5f5f5' }}>
                        <span style={{ fontFamily: 'monospace', color: '#3B3A8F', fontWeight: 700 }}>{l.lot_no}</span>
                        <span style={{ color: '#888' }}>{l.lot_type}{l.knitter_name ? ' · ' + l.knitter_name : ''}</span>
                        <span>in {num(l.gross_in_kg).toLocaleString('en-IN')} → out <strong>{num(l.net_out_kg).toLocaleString('en-IN')}</strong></span>
                      </div>
                    ))}
                  </div>
                  {/* Wastage is COMPUTED, never stored. Norm shown only where one
                      exists — dyeing and finishing norms are outstanding and must
                      not silently inherit knitting's 0.5% (§I-3). */}
                  <div style={{ padding: '6px 10px', fontSize: 11, background: '#FAFAFA', borderTop: '1px solid #eee' }}>
                    Issue variance {num(recon.issue_variance_kg).toLocaleString('en-IN')} {jw.unit}
                    {recon.issue_variance_pct != null && ` (${recon.issue_variance_pct}%)`} — tolerance, recorded not enforced
                    {recon.wastage_pct != null && (
                      <> · wastage <strong style={{ color: recon.within_norm === false ? '#A32D2D' : '#166534' }}>{recon.wastage_pct}%</strong>
                        {recon.norm_pct != null ? ` vs norm ${recon.norm_pct}%` : ' · no norm set for this process'}</>
                    )}
                  </div>
                </>)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          {/* Disabled only while in flight. It used to grey whenever lots were missing, with the
              reason in a title tooltip — invisible on a touch screen and to anyone who does not
              hover. Since mig 292 a missing lot is not a dead end anyway: it is the typed path,
              and save() names whatever is actually unmet. */}
          <button disabled={busy} onClick={save}
            style={{ ...btnPrimary, background: busy ? '#e5e7eb' : '#166534', color: busy ? '#aaa' : 'white',
                     cursor: busy ? 'not-allowed' : 'pointer' }}>{busy ? 'Raising…' : '📥 Raise GRN'}</button>
        </div>
      </div>
    </div>
  );
}

function QualityModal({ grn, onClose, onSaved }) {
  const [form, setForm] = useState({ quality_status: '', quality_remarks: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setError('');
    // WRITTEN, NOT JUST MADE REACHABLE. This handler had no message for a missing verdict at
    // all — the button simply greyed. The only sentence anywhere near it belongs to the GRN
    // modal above and is about a date, so a reader hunting for the reason found a wrong one,
    // which is worse than finding none.
    if (!form.quality_status) {
      setError('Pick a verdict — passed, failed or conditional — before recording it.'); return;
    }
    setBusy(true);
    try { await api.post(`/jobwork/${grn.jwo_id}/quality`, form); onSaved(); alert(`✅ Quality ${form.quality_status.toUpperCase()} recorded for ${grn.grn_no}.`); }
    catch (err) { setError(err.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 440, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>✍ Quality verdict — {grn.grn_no}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888' }}>{grn.jwo_no} · {grn.vendor_name} · received {num(grn.qty_received).toLocaleString('en-IN')} {grn.unit} by {grn.received_by_name || '—'}</p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div><label style={labelStyle}>Verdict *</label>
          <select style={{ ...inputStyle, width: '100%' }} value={form.quality_status} onChange={e => setForm({ ...form, quality_status: e.target.value })}>
            <option value="">— select —</option><option value="passed">Passed</option><option value="failed">Failed</option><option value="conditional">Conditional</option>
          </select></div>
        <div style={{ marginTop: 12 }}><label style={labelStyle}>Remarks</label><input style={{ ...inputStyle, width: '100%' }} value={form.quality_remarks} onChange={e => setForm({ ...form, quality_remarks: e.target.value })} /></div>
        <p style={{ margin: '12px 0 0', fontSize: 11, color: '#888' }}>This verdict raises the GRN and unblocks invoicing. It cannot be changed afterwards.</p>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save}
            style={{ ...btnPrimary, background: busy ? '#e5e7eb' : '#166534', color: busy ? '#aaa' : 'white' }}>✍ Record verdict</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceModal({ jw, onClose, onSaved }) {
  // The TAXABLE value is computed server-side as qty × rate (mig 133) — the agreed rate is GST-free. The GST rate and
  // the SAC are read off the vendor's bill (mig 413; Rajan, 20 Oct 2026): the rate varies by item, so there is no
  // master to read it from. The payable is taxable + GST; the billed total is compared against that, never paid from.
  const taxable = +(num(jw.qty_received) * num(jw.rate)).toFixed(2);
  const [form, setForm] = useState({ invoice_date: '', gst_rate_pct: '', sac_code: '' });   // the date is stated, never prefilled (dates change 2)
  const rate = form.gst_rate_pct === '' ? null : Number(form.gst_rate_pct);
  const gst = rate === null ? null : Math.round(taxable * rate) / 100;
  const payable = gst === null ? null : +(taxable + gst).toFixed(2);
  const claimed = form.invoice_amount === '' || form.invoice_amount == null ? null : num(form.invoice_amount);
  const diff = claimed === null || payable === null ? null : +(claimed - payable).toFixed(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [said, setSaid] = useState(null);   // the server's reconciliation, shown after saving
  const inr = (x) => `₹${Number(x).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await api.post(`/jobwork/${jw.id}/invoice`, form);
      const rec = r.data?.reconciliation;
      if (rec && rec.agrees === false) { setSaid(rec.says); setBusy(false); return; }   // saved; say it before closing
      onSaved();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 460, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>🧾 Upload Invoice — {jw.jwo_no}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888' }}>{jw.vendor_name} · JWO value ₹{num(jw.total_amount).toLocaleString('en-IN')} (GST-free)</p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {said && (
          <div style={{ background: '#FFF8E6', border: '1px solid #F5C77E', color: '#7A4B00', padding: '10px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>
            <strong>Saved — but the bill does not agree.</strong> {said}
            <div style={{ marginTop: 8, textAlign: 'right' }}><button onClick={onSaved} style={{ ...btnPrimary, padding: '5px 14px', fontSize: 12 }}>OK</button></div>
          </div>)}
        <div style={{ background: '#F2F6FF', border: '1px solid #D6E2FA', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#5A6B8C' }}>
            <span>Taxable value</span><strong style={{ color: '#2D2C72' }}>{inr(taxable)}</strong></div>
          <div style={{ fontSize: 11, color: '#7A88A3', marginTop: 2 }}>
            {num(jw.qty_received).toLocaleString('en-IN')} {jw.unit || ''} received × ₹{num(jw.rate)}
            {num(jw.shortage) > 0 && <span style={{ color: '#A32D2D' }}> · {num(jw.shortage).toLocaleString('en-IN')} short, not billed</span>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#5A6B8C', marginTop: 6 }}>
            <span>GST {rate === null ? '— enter the rate on the bill' : `${rate}%`}</span><strong style={{ color: '#2D2C72' }}>{gst === null ? '—' : inr(gst)}</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#2D2C72', marginTop: 6, borderTop: '1px solid #D6E2FA', paddingTop: 6 }}>
            <span style={{ fontWeight: 700 }}>Payable (taxable + GST)</span><strong style={{ fontSize: 18 }}>{payable === null ? '—' : inr(payable)}</strong></div>
          <div style={{ fontSize: 10, color: '#7A88A3', marginTop: 4 }}>CGST+SGST or IGST is worked out from the vendor's and the company's GSTINs when you save.</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={labelStyle}>Invoice No *</label><input style={{ ...inputStyle, width: '100%' }} value={form.invoice_no || ''} onChange={e => setForm({ ...form, invoice_no: e.target.value })} /></div>
          <div><label style={labelStyle}>Invoice Date</label><input type="date" style={{ ...inputStyle, width: '100%' }} value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} /></div>
          <div><label style={labelStyle}>GST rate on the bill *</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.gst_rate_pct} onChange={e => setForm({ ...form, gst_rate_pct: e.target.value })}>
              <option value="">— select —</option>
              {[5, 18, 12, 0, 0.25, 3, 28].map(x => <option key={x} value={x}>{x}%{x === 0 ? ' (no GST on the bill)' : ''}</option>)}
            </select></div>
          <div><label style={labelStyle}>SAC on the bill *</label>
            <input style={{ ...inputStyle, width: '100%' }} placeholder="6 digits, starts 99" maxLength={6} value={form.sac_code} onChange={e => setForm({ ...form, sac_code: e.target.value.replace(/\D/g, '') })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Vendor billed total (₹, with GST)</label>
            <input type="number" step="0.01" placeholder="the bill's grand total" style={{ ...inputStyle, width: '100%' }} value={form.invoice_amount || ''} onChange={e => setForm({ ...form, invoice_amount: e.target.value })} />
            {diff !== null && (Math.abs(diff) < 0.005
              ? <div style={{ fontSize: 11, marginTop: 4, color: '#1B7A46' }}>✓ Agrees with taxable + GST</div>
              : <div style={{ fontSize: 11, marginTop: 4, color: '#A32D2D' }}>
                  Differs from taxable + GST by {inr(Math.abs(diff))} ({diff > 0 ? 'billed more' : 'billed less'}) — the payable stays {inr(payable)}. Check the rate, the quantity or the bill.
                </div>)}
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Invoice PDF</label>
            <FileBtn label={form.file_url ? '✅ uploaded — replace' : '📎 Upload invoice PDF'} onUploaded={(u) => setForm({ ...form, file_url: u })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy || !!said} onClick={save} style={btnPrimary}>{busy ? 'Saving…' : '🧾 Save Invoice'}</button>
        </div>
      </div>
    </div>
  );
}

// GST ENTERED LATER (mig 413; Rajan, 20 Oct 2026). For an invoice entered before GST was recorded, and only while it
// awaits certification: the rate and SAC are read off the same bill, once. Whoever records it may not then certify the
// invoice; the server says so if they try.
function GstLaterModal({ inv, onClose, onSaved }) {
  const taxable = num(inv.invoice_amount);
  const [form, setForm] = useState({ gst_rate_pct: '', sac_code: '' });
  const rate = form.gst_rate_pct === '' ? null : Number(form.gst_rate_pct);
  const gst = rate === null ? null : Math.round(taxable * rate) / 100;
  const payable = gst === null ? null : +(taxable + gst).toFixed(2);
  const claimed = inv.invoice_amount_claimed == null ? null : num(inv.invoice_amount_claimed);
  const diff = claimed === null || payable === null ? null : +(claimed - payable).toFixed(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inr = (x) => `₹${Number(x).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const save = async () => {
    setBusy(true); setError('');
    try { await api.post(`/jobwork/invoices/${inv.id}/gst`, form); onSaved(); }
    catch (err) { setError(err.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 420, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Record GST — invoice {inv.invoice_no}</h3></div>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#666' }}>
          This invoice was entered before GST was recorded. Read the rate and SAC off the same bill. It is recorded <strong>once</strong>, and only while the invoice awaits certification. Whoever records it cannot certify it.
        </p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ background: '#F2F6FF', border: '1px solid #D6E2FA', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#5A6B8C' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Taxable value</span><strong style={{ color: '#2D2C72' }}>{inr(taxable)}</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}><span>GST {rate === null ? '—' : `${rate}%`}</span><strong style={{ color: '#2D2C72' }}>{gst === null ? '—' : inr(gst)}</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, borderTop: '1px solid #D6E2FA', paddingTop: 6, fontSize: 14 }}><span style={{ fontWeight: 700 }}>Payable (taxable + GST)</span><strong>{payable === null ? '—' : inr(payable)}</strong></div>
          {claimed !== null && diff !== null && (Math.abs(diff) < 0.005
            ? <div style={{ marginTop: 6, color: '#1B7A46' }}>✓ Agrees with the billed {inr(claimed)}</div>
            : <div style={{ marginTop: 6, color: '#A32D2D' }}>Billed {inr(claimed)} — differs from taxable + GST by {inr(Math.abs(diff))}</div>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={labelStyle}>GST rate on the bill *</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.gst_rate_pct} onChange={e => setForm({ ...form, gst_rate_pct: e.target.value })}>
              <option value="">— select —</option>
              {[5, 18, 12, 0, 0.25, 3, 28].map(x => <option key={x} value={x}>{x}%{x === 0 ? ' (no GST on the bill)' : ''}</option>)}
            </select></div>
          <div><label style={labelStyle}>SAC on the bill *</label>
            <input style={{ ...inputStyle, width: '100%' }} placeholder="6 digits, starts 99" maxLength={6} value={form.sac_code} onChange={e => setForm({ ...form, sac_code: e.target.value.replace(/\D/g, '') })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? 'Saving…' : 'Record GST'}</button>
        </div>
      </div>
    </div>
  );
}
